import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import ConfirmDestructive from '../components/ConfirmDestructive';
import { useAuth } from '../context/AuthContext';
import {
  ordenCompraService, tipoCambioService, serieService, articuloService,
  entidadService, trabajadorService, almacenService, formasPagoService,
  tablaService, datosGeneralesService, requerimientoService, parteEntradaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import { useCatalogSync } from '../utils/eventBus';
import '../styles/datatable.css';
import '../styles/ordenes_compra.css';
import '../styles/requerimientos.css';

const CATALOG_TOPICS_OC = [
  'entidades', 'articulos', 'trabajadores', 'almacenes',
  'formas-pago', 'tipos-documento', 'series', 'centros-costo',
];

const today = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; };
const fmtMon = (v, dec = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmt4 = (v) => (parseFloat(v) || 0).toFixed(4);

const ESTADO_COLORS = {
  PENDIENTE: 'pendiente', APROBADO: 'aprobado',
  DESAPROBADO: 'desaprobado', ANULADO: 'anulado',
};

const TIPOS_DOC_ENTIDAD = [
  { codigo: '6', label: 'RUC' }, { codigo: '1', label: 'DNI' },
  { codigo: '4', label: 'Carnet de Extranjería' }, { codigo: '7', label: 'Pasaporte' },
  { codigo: '0', label: 'Otros' },
];

// Columnas de la plantilla Excel de importación masiva de OC.
// Cada fila es un ítem; el agrupador es (Sucursal, Serie, Numero).
// Serie_RQ_Origen + Numero_RQ_Origen (opcionales) ligan el ítem a su RQ.
const OC_IMPORT_COLUMNS = [
  { key: 'Sucursal',              label: 'Sucursal',              required: true,  example: 'PRIN',       description: 'Código o nombre de la sucursal' },
  { key: 'Serie',                 label: 'Serie',                 required: true,  example: '0001',       description: 'Serie OC de 4 dígitos' },
  { key: 'Numero',                label: 'Numero',                required: true,  example: '42',         description: 'Número correlativo de la OC' },
  { key: 'Fecha_Emision',         label: 'Fecha_Emision',         required: true,  example: '2026-04-22', description: 'YYYY-MM-DD o fecha Excel' },
  { key: 'Fecha_Entrega',         label: 'Fecha_Entrega',         required: false, example: '2026-05-05', description: 'Opcional' },
  { key: 'Tipo_OC',               label: 'Tipo_OC',               required: true,  example: 'NACIONAL',   description: 'NACIONAL o IMPORTACION' },
  { key: 'Moneda',                label: 'Moneda',                required: true,  example: 'PEN',        description: 'PEN o USD' },
  { key: 'Tipo_Cambio',           label: 'Tipo_Cambio',           required: false, example: '3.7500',     description: 'Requerido si Moneda = USD' },
  { key: 'RUC_Proveedor',         label: 'RUC_Proveedor',         required: true,  example: '20123456789', description: 'RUC del proveedor' },
  { key: 'Codigo_Forma_Pago',     label: 'Codigo_Forma_Pago',     required: true,  example: 'CONTADO',    description: 'Código de la forma de pago' },
  { key: 'DNI_Solicitante',       label: 'DNI_Solicitante',       required: false, example: '12345678',   description: 'DNI del solicitante (opcional)' },
  { key: 'Codigo_Almacen_Entrega',label: 'Codigo_Almacen_Entrega',required: false, example: 'PRIN-01',    description: 'Código del almacén de entrega (opcional)' },
  { key: 'Codigo_Tipo_Doc_Ref1',  label: 'Codigo_Tipo_Doc_Ref1',  required: true,  example: 'FAC',        description: 'Código de tipo de documento de referencia 1' },
  { key: 'Numero_Doc_Ref1',       label: 'Numero_Doc_Ref1',       required: true,  example: 'F001-123',   description: 'Número de documento de referencia 1' },
  { key: 'Observacion_Cabecera',  label: 'Observacion_Cabecera',  required: false, example: 'Compra urgente', description: 'Observación general (opcional)' },
  { key: 'Estado',                label: 'Estado',                required: false, example: 'PENDIENTE',  description: 'PENDIENTE | APROBADO | DESAPROBADO | ANULADO (default PENDIENTE; para migración histórica)' },
  { key: 'Codigo_Articulo',       label: 'Codigo_Articulo',       required: true,  example: 'ART001',     description: 'Código interno del artículo' },
  { key: 'Cantidad',              label: 'Cantidad',              required: true,  example: '5',         description: 'Cantidad (> 0)' },
  { key: 'Precio_Unitario',       label: 'Precio_Unitario',       required: true,  example: '12.5000',    description: 'Precio unitario SIN IGV' },
  { key: 'Porcentaje_IGV',        label: 'Porcentaje_IGV',        required: false, example: '18',         description: 'Porcentaje de IGV (default 18)' },
  { key: 'Porcentaje_Descuento',  label: 'Porcentaje_Descuento',  required: false, example: '0',          description: 'Porcentaje de descuento del ítem (default 0)' },
  { key: 'Codigo_Centro_Costo',   label: 'Codigo_Centro_Costo',   required: true,  example: 'CC-01',      description: 'Código del centro de costo (obligatorio en OC)' },
  { key: 'Observacion_Item',      label: 'Observacion_Item',      required: false, example: 'Urgente',    description: 'Observación del ítem (opcional)' },
  { key: 'Serie_RQ_Origen',       label: 'Serie_RQ_Origen',       required: false, example: '0001',       description: 'Trazabilidad: serie del RQ origen' },
  { key: 'Numero_RQ_Origen',      label: 'Numero_RQ_Origen',      required: false, example: '15',         description: 'Trazabilidad: número del RQ origen (el RQ debe estar APROBADO)' },
];

const EMPTY_LINEA = {
  id_articulo: null, cantidad: 1,
  precio_soles: '', precio_dolares: '', porcentaje_igv: 18,
  igv_soles: 0, igv_dolares: 0, total_soles: 0, total_dolares: 0,
  porcentaje_descuento: 0, descuento_soles: 0, descuento_dolares: 0,
  total_con_descuento_soles: 0, total_con_descuento_dolares: 0, observacion: '',
  id_centro_costo: null,
};

function calcLinea(linea, tc, descuentoCab) {
  const cant = parseFloat(linea.cantidad) || 0;
  const pct_igv = parseFloat(linea.porcentaje_igv) ?? 18;
  const pct_desc = parseFloat(linea.porcentaje_descuento) ?? parseFloat(descuentoCab) ?? 0;
  const precioS = parseFloat(linea.precio_soles) || 0;
  const precioD = parseFloat(linea.precio_dolares) || 0;

  const igvS = parseFloat((precioS * pct_igv / 100).toFixed(4));
  const igvD = parseFloat((precioD * pct_igv / 100).toFixed(4));
  const totalUnitS = parseFloat((precioS + igvS).toFixed(4));
  const totalUnitD = parseFloat((precioD + igvD).toFixed(4));
  // descuento = cantidad × (precio + IGV) × %desc
  const descS = parseFloat((cant * totalUnitS * pct_desc / 100).toFixed(4));
  const descD = parseFloat((cant * totalUnitD * pct_desc / 100).toFixed(4));
  const totalLineaS = parseFloat((cant * totalUnitS - descS).toFixed(4));
  const totalLineaD = parseFloat((cant * totalUnitD - descD).toFixed(4));

  return {
    ...linea,
    porcentaje_descuento: pct_desc,
    igv_soles: igvS, igv_dolares: igvD,
    total_soles: totalUnitS, total_dolares: totalUnitD,
    descuento_soles: descS, descuento_dolares: descD,
    total_con_descuento_soles: totalLineaS,
    total_con_descuento_dolares: totalLineaD,
  };
}

export default function OrdenesCompra() {
  const { sucursal } = useAuth();
  const location = useLocation();

  // ── Vista: 'list' | 'form' ──
  const [view, setView] = useState('list');

  // ── Lista ──
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ sucursal: '', serie: '', numero: '', fechaInicio: weekAgo(), fechaFin: today(), proveedor: '', estado: '', tipoDocRef1: '', numDocRef1: '' });

  // ── UI ──
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // ── Modal Ver OC ──
  const [viewModal, setViewModal]     = useState(null);
  const [viewData, setViewData]       = useState(null);
  const [loadingView, setLoadingView] = useState(false);

  // ── Modal Origen RQ ──
  const [origenModal, setOrigenModal] = useState(null); // row de OC
  const [origenRQs, setOrigenRQs]     = useState([]);   // [{id_rq, numero, serie}]
  const [loadingOrigen, setLoadingOrigen] = useState(false);

  // ── Modal Ingreso PE ──
  const [peModal, setPeModal]           = useState(null); // row de OC
  const [peAtencionData, setPeAtencionData] = useState([]);
  const [loadingPe, setLoadingPe]       = useState(false);

  // ── Modal Proveedor (crear nuevo desde OC) ──
  const emptyProv = { tipo_documento: '6', ruc: '', razon_social: '', direccion: '', correo1: '', telefono_contacto: '', celular: '', persona_contacto: '' };
  const [modalProv, setModalProv]           = useState(false);
  const [savingProv, setSavingProv]         = useState(false);
  const [consultandoRuc, setConsultandoRuc] = useState(false);
  const [formProv, setFormProv]             = useState(emptyProv);

  // ── Catálogos ──
  const [series, setSeries] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);

  // ── Formulario ──
  const [editing, setEditing] = useState(null);
  const [tcSource, setTcSource] = useState('');
  const [form, setForm] = useState(null);
  const [detalle, setDetalle] = useState([]);
  const [saving, setSaving] = useState(false);
  // Toggle: captura el precio unitario con IGV incluido (true) o sin IGV (false).
  // Internamente siempre se guarda precio_soles/precio_dolares SIN IGV; el toggle solo afecta captura y visualización.
  const [precioConIgv, setPrecioConIgv] = useState(false);

  // ── DataTable refs ──
  const exportRef = useRef(null);
  const importRef = useRef(null);
  const templateRef = useRef(null);

  const makeEmptyForm = useCallback(() => ({
    id_sucursal: sucursal?.id_sucursal || null,
    sucursal_nombre: sucursal?.nombre || '',
    tipo_documento: 'OC',
    id_serie: '', numero: '',
    tipo_oc: 'NACIONAL',
    fecha_emision: today(), fecha_entrega: today(),
    moneda: 'PEN', tipo_cambio: 1,
    id_proveedor: null, proveedor_nombre: '', proveedor_ruc: '', proveedor_direccion: '',
    id_forma_pago: null, id_solicitante: null,
    descuento_porcentaje: 0,
    id_almacen_entrega: null,
    id_tipo_doc_ref1: null, num_doc_ref1: '',
    id_tipo_doc_ref2: null, num_doc_ref2: '',
    id_persona_atencion: null, id_almacen_factura: null,
    observaciones: '',
    id_centro_costo: null, // CC de cabecera (se propaga a líneas manuales)
  }), [sucursal]);

  // ── Fetch lista ──
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ordenCompraService.getAll();
      setRecords(r.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar órdenes de compra.' });
    } finally { setLoading(false); }
  }, []);

  // ── Fetch catálogos ──
  const fetchCatalogos = useCallback(async () => {
    try {
      // Los artículos se buscan remotamente (AutoComplete async), ya no se precargan.
      const [ser, ent, trab, alm, fp, td, cc] = await Promise.all([
        serieService.getAll('OC', sucursal?.id_sucursal),
        entidadService.getAll(),
        trabajadorService.getAll(),
        almacenService.getAll(),
        formasPagoService.getAll(),
        tablaService('tipos-documento').getAll(),
        tablaService('centros-costo').getAll(),
      ]);
      setSeries(ser.data || []);
      setArticulos([]);
      setEntidades(ent.data || []);
      setTrabajadores(trab.data || []);
      setAlmacenes(alm.data || []);
      setFormasPago(fp.data || []);
      setTiposDoc(td.data || []);
      setCentrosCosto((cc.data || []).map(c => ({ ...c, display_cc: `${c.codigo} — ${c.descripcion}` })));
    } catch {}
  }, [sucursal?.id_sucursal]);

  useEffect(() => { fetchAll(); fetchCatalogos(); }, [fetchAll, fetchCatalogos]);

  // Refetch catálogos cuando otros flujos (o este mismo) crean/editan entidades, articulos, etc.
  useCatalogSync(CATALOG_TOPICS_OC, fetchCatalogos);
  useCatalogSync('ordenes-compra', fetchAll);

  // ─── Importación masiva de OC ────────────────────────────────────
  useEffect(() => {
    const HEADER_FIELDS = [
      'Fecha_Emision', 'Fecha_Entrega', 'Tipo_OC', 'Moneda', 'Tipo_Cambio',
      'RUC_Proveedor', 'Codigo_Forma_Pago', 'DNI_Solicitante',
      'Codigo_Almacen_Entrega', 'Codigo_Tipo_Doc_Ref1', 'Numero_Doc_Ref1',
      'Observacion_Cabecera', 'Estado',
    ];

    const normFecha = (v) => {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number') {
        const d = new Date(Math.round((v - 25569) * 86400 * 1000));
        return d.toISOString().split('T')[0];
      }
      const s = String(v).trim();
      if (!s) return null;
      return s.includes('T') ? s.split('T')[0] : s;
    };
    const normStr = (v) => (v === undefined || v === null) ? '' : String(v).trim();

    window._dtImportHandler = async (rows) => {
      if (!rows || rows.length === 0) return;

      // 1. Agrupar por Sucursal + Serie + Numero
      const groups = new Map();
      const keyOf = (r) => {
        const suc = normStr(r.Sucursal).toUpperCase();
        const ser = normStr(r.Serie).toUpperCase().padStart(4, '0');
        const num = parseInt(normStr(r.Numero).replace(/^0+/, '')) || 0;
        return `${suc}|${ser}|${num}`;
      };
      rows.forEach((row, i) => {
        const k = keyOf(row);
        if (!groups.has(k)) groups.set(k, { header: row, filas: [] });
        groups.get(k).filas.push({ row, idx: i });
      });

      // 2. Validar consistencia de cabecera dentro de cada grupo
      const inconsistencias = [];
      for (const [k, g] of groups) {
        const h = g.header;
        for (const { row, idx } of g.filas) {
          for (const f of HEADER_FIELDS) {
            const a = normStr(row[f]);
            const b = normStr(h[f]);
            if (a !== b) {
              inconsistencias.push(`Grupo ${k}: fila ${idx + 1} difiere en "${f}" ("${a}" vs. "${b}")`);
              break;
            }
          }
        }
      }

      // 3. Si hay inconsistencias, abortar importación completa
      if (inconsistencias.length > 0) {
        const total = groups.size;
        let i = 0;
        for (const msg of inconsistencias) {
          i++;
          if (window._dtImportProgress) window._dtImportProgress(i, total, 0, i, msg);
        }
        if (window._dtImportProgress) window._dtImportProgress(total, total, 0, total, 'Importación abortada: corrige las inconsistencias y reintenta.');
        return;
      }

      // 4. Enviar cada grupo al backend
      let ok = 0, fail = 0;
      const total = groups.size;
      let procesados = 0;
      for (const [, g] of groups) {
        procesados++;
        const h = g.header;
        const payload = {
          codigo_sucursal:        normStr(h.Sucursal),
          serie:                  normStr(h.Serie).padStart(4, '0'),
          numero:                 normStr(h.Numero),
          fecha_emision:          normFecha(h.Fecha_Emision),
          fecha_entrega:          normFecha(h.Fecha_Entrega) || null,
          tipo_oc:                normStr(h.Tipo_OC).toUpperCase(),
          moneda:                 normStr(h.Moneda).toUpperCase(),
          tipo_cambio:            parseFloat(h.Tipo_Cambio) || 1,
          ruc_proveedor:          normStr(h.RUC_Proveedor),
          codigo_forma_pago:      normStr(h.Codigo_Forma_Pago),
          dni_solicitante:        normStr(h.DNI_Solicitante) || null,
          codigo_almacen_entrega: normStr(h.Codigo_Almacen_Entrega) || null,
          codigo_tipo_doc_ref1:   normStr(h.Codigo_Tipo_Doc_Ref1),
          numero_doc_ref1:        normStr(h.Numero_Doc_Ref1),
          observaciones:          normStr(h.Observacion_Cabecera) || null,
          estado:                 normStr(h.Estado).toUpperCase() || null,
          items: g.filas.map(({ row }) => ({
            codigo_articulo:      normStr(row.Codigo_Articulo),
            cantidad:             parseFloat(row.Cantidad) || 0,
            precio_unitario:      parseFloat(row.Precio_Unitario) || 0,
            porcentaje_igv:       row.Porcentaje_IGV === undefined || row.Porcentaje_IGV === '' ? 18 : parseFloat(row.Porcentaje_IGV),
            porcentaje_descuento: parseFloat(row.Porcentaje_Descuento) || 0,
            codigo_centro_costo:  normStr(row.Codigo_Centro_Costo),
            observacion:          normStr(row.Observacion_Item) || null,
            serie_rq_origen:      normStr(row.Serie_RQ_Origen) || null,
            numero_rq_origen:     normStr(row.Numero_RQ_Origen) || null,
          })),
        };
        let errorMsg = null;
        try {
          await ordenCompraService.importGrupo(payload);
          ok++;
        } catch (err) {
          fail++;
          errorMsg = `OC ${payload.serie}-${payload.numero}: ${getErrorDetail(err, 'Error al importar')}`;
        }
        if (window._dtImportProgress) window._dtImportProgress(procesados, total, ok, fail, errorMsg);
      }
      fetchAll();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchAll]);

  // ── Leer items pre-cargados desde OcDesdeRq ──
  useEffect(() => {
    if (location.state?.fromRq) {
      const raw = sessionStorage.getItem('oc_desde_rq');
      sessionStorage.removeItem('oc_desde_rq');
      if (!raw) return;
      try {
        const items = JSON.parse(raw);
        setEditing(null);
        const ccIds = new Set(items.map(it => it.id_centro_costo || null));
        const ccComun = ccIds.size === 1 ? [...ccIds][0] || null : null;
        setForm({ ...makeEmptyForm(), id_centro_costo: ccComun });
        setTcSource('');
        const lineas = items.map((it, i) => calcLinea({
          id_articulo: it.id_articulo,
          articulo_descripcion: it.articulo_descripcion,
          articulo_codigo: it.articulo_codigo,
          unidad_descripcion: it.unidad_descripcion || '',
          cantidad: it.cantidad_a_usar || 1,
          precio_soles: '',
          precio_dolares: '',
          porcentaje_igv: it.porcentaje_igv_compra ?? 18,
          porcentaje_descuento: 0,
          observacion: it.observacion || '',
          id_requerimiento: it.id_requerimiento,
          id_requerimiento_detalle: it.id_detalle,
          rq_serie: it.rq_serie || '',
          rq_numero: it.rq_numero || '',
          id_centro_costo: it.id_centro_costo || null,
          centro_costo_codigo: it.centro_costo_codigo || '',
          centro_costo_descripcion: it.centro_costo_descripcion || '',
        }, 1, 0));
        setDetalle(lineas);
        setView('form');
      } catch {}
    }
  }, []); // eslint-disable-line

  // ── Buscar tipo de cambio ──
  const buscarTC = useCallback(async (fecha, moneda) => {
    if (!fecha) return;
    try {
      const r = await tipoCambioService.getByFecha(fecha);
      const tc = r.data?.tc_venta || r.data?.tc_compra;
      if (tc) {
        setForm(prev => ({ ...prev, tipo_cambio: parseFloat(tc) }));
        setTcSource('db');
        return;
      }
    } catch {}
    try {
      const d = new Date(fecha + 'T00:00:00');
      const r = await tipoCambioService.consultarSunat(d.getFullYear(), d.getMonth() + 1);
      const item = (r.data || []).find(x => x.fecha === fecha);
      if (item?.venta) {
        const tc = parseFloat(item.venta);
        try { await tipoCambioService.create({ fecha, tc_compra: parseFloat(item.compra || item.venta), tc_venta: tc }); } catch {}
        setForm(prev => ({ ...prev, tipo_cambio: tc }));
        setTcSource('sunat');
        return;
      }
    } catch {}
    setForm(prev => ({ ...prev, tipo_cambio: moneda === 'PEN' ? 1 : '' }));
    setTcSource('manual');
  }, []);

  // ── Cuando cambia fecha o moneda en el form, buscar TC ──
  useEffect(() => {
    if (view === 'form' && !editing && form?.fecha_emision) {
      buscarTC(form.fecha_emision, form.moneda);
    }
  }, [form?.fecha_emision, form?.moneda, view, editing, buscarTC]);

  // ── Generar número (cálculo local, backend es quien incrementa al crear) ──
  const generarNumero = useCallback((id_serie) => {
    if (!id_serie) { setForm(prev => ({ ...prev, numero: '' })); return; }
    const s = series.find(x => String(x.id_serie) === String(id_serie));
    if (!s) return;
    const next = (s.ultimo_numero || 0) + 1;
    setForm(prev => ({ ...prev, numero: String(next).padStart(s.maximo_numeros || 7, '0') }));
  }, [series]);

  // ── Totales calculados ──
  const totales = useMemo(() => {
    let baseS = 0, igvS = 0, noGravS = 0, descS = 0, totalS = 0;
    let baseD = 0, igvD = 0, noGravD = 0, descD = 0, totalD = 0;
    detalle.forEach(l => {
      const cant = parseFloat(l.cantidad) || 0;
      const precioS = parseFloat(l.precio_soles) || 0;
      const precioD = parseFloat(l.precio_dolares) || 0;
      const pctIgv = parseFloat(l.porcentaje_igv) ?? 18;
      if (pctIgv === 0) {
        noGravS += cant * precioS;
        noGravD += cant * precioD;
      } else {
        baseS += cant * precioS;
        baseD += cant * precioD;
        igvS += cant * precioS * pctIgv / 100;
        igvD += cant * precioD * pctIgv / 100;
      }
      descS += parseFloat(l.descuento_soles) || 0;
      descD += parseFloat(l.descuento_dolares) || 0;
      totalS += parseFloat(l.total_con_descuento_soles) || 0;
      totalD += parseFloat(l.total_con_descuento_dolares) || 0;
    });
    return {
      base_imponible_soles: parseFloat(baseS.toFixed(4)),
      igv_soles: parseFloat(igvS.toFixed(4)),
      no_gravado_soles: parseFloat(noGravS.toFixed(4)),
      descuento_soles: parseFloat(descS.toFixed(4)),
      total_soles: parseFloat(totalS.toFixed(4)),
      base_imponible_dolares: parseFloat(baseD.toFixed(4)),
      igv_dolares: parseFloat(igvD.toFixed(4)),
      no_gravado_dolares: parseFloat(noGravD.toFixed(4)),
      descuento_dolares: parseFloat(descD.toFixed(4)),
      total_dolares: parseFloat(totalD.toFixed(4)),
    };
  }, [detalle]);

  // ── Opciones para filtros autocomplete (derivadas de records) ──
  const filterSucursales = useMemo(() => {
    const seen = new Set();
    return records.filter(r => r.sucursal_nombre && !seen.has(r.sucursal_nombre) && seen.add(r.sucursal_nombre))
      .map(r => ({ id: r.sucursal_nombre, nombre: r.sucursal_nombre }));
  }, [records]);

  const filterSeries = useMemo(() => {
    const seen = new Set();
    return records.filter(r => r.serie_codigo && !seen.has(r.serie_codigo) && seen.add(r.serie_codigo))
      .map(r => ({ id: r.serie_codigo, serie: r.serie_codigo }));
  }, [records]);

  // CC agrupado del detalle: id único si todos coinciden, null si hay varios o faltan
  const ccUnicoId = useMemo(() => {
    if (!detalle.length) return null;
    const ids = new Set(detalle.map(l => l.id_centro_costo || null));
    if (ids.size === 1) {
      const v = [...ids][0];
      return v || null;
    }
    return null;
  }, [detalle]);

  const ccAgrupadoLabel = useMemo(() => {
    const etiquetas = new Set();
    detalle.forEach(l => {
      if (l.id_centro_costo) {
        const cc = centrosCosto.find(c => c.id_centro_costo === l.id_centro_costo);
        if (cc) etiquetas.add(`${cc.codigo} — ${cc.descripcion}`);
        else if (l.centro_costo_codigo) etiquetas.add(`${l.centro_costo_codigo} — ${l.centro_costo_descripcion || ''}`);
      }
    });
    return [...etiquetas].join(', ');
  }, [detalle, centrosCosto]);

  // CC de cabecera → aplica a todos los ítems MANUALES (los que vienen de RQ se respetan).
  const aplicarCCCabecera = (idCc) => {
    if (!idCc) {
      // Permite limpiar a mano sólo las líneas manuales
      setDetalle(prev => prev.map(l => l.id_requerimiento_detalle ? l : { ...l, id_centro_costo: null }));
      return;
    }
    setDetalle(prev => prev.map(l => l.id_requerimiento_detalle ? l : { ...l, id_centro_costo: idCc }));
  };

  const filterProveedores = useMemo(() => {
    const seen = new Set();
    return records.filter(r => r.proveedor_nombre && !seen.has(r.proveedor_nombre) && seen.add(r.proveedor_nombre))
      .map(r => ({ id: r.proveedor_nombre, nombre: r.proveedor_nombre, ruc: r.proveedor_ruc || '' }));
  }, [records]);

  const filterTiposDocRef1 = useMemo(() => {
    const seen = new Set();
    return records.filter(r => r.tipo_doc_ref1_descripcion && !seen.has(r.tipo_doc_ref1_descripcion) && seen.add(r.tipo_doc_ref1_descripcion))
      .map(r => ({ id: r.tipo_doc_ref1_descripcion, nombre: r.tipo_doc_ref1_descripcion }));
  }, [records]);

  // ── Filtrar ──
  const filteredRecords = useMemo(() => {
    let r = records;
    if (filters.sucursal) { const q = filters.sucursal.toLowerCase(); r = r.filter(x => (x.sucursal_nombre || '').toLowerCase().includes(q)); }
    if (filters.serie) { const q = filters.serie.toLowerCase(); r = r.filter(x => (x.serie_codigo || '').toLowerCase().includes(q)); }
    if (filters.numero) { const q = filters.numero.toLowerCase(); r = r.filter(x => (x.numero || '').toLowerCase().includes(q)); }
    if (filters.fechaInicio) r = r.filter(x => (x.fecha_emision || '').slice(0, 10) >= filters.fechaInicio);
    if (filters.fechaFin) r = r.filter(x => (x.fecha_emision || '').slice(0, 10) <= filters.fechaFin);
    if (filters.proveedor) { const q = filters.proveedor.toLowerCase(); r = r.filter(x => (x.proveedor_nombre || '').toLowerCase().includes(q)); }
    if (filters.estado) r = r.filter(x => x.estado === filters.estado);
    if (filters.tipoDocRef1) { const q = filters.tipoDocRef1.toLowerCase(); r = r.filter(x => (x.tipo_doc_ref1_descripcion || '').toLowerCase().includes(q)); }
    if (filters.numDocRef1)  { const q = filters.numDocRef1.toLowerCase();  r = r.filter(x => (x.num_doc_ref1 || '').toLowerCase().includes(q)); }
    return r;
  }, [records, filters]);

  // ── Detalle helpers ──
  const agregarLinea = () => setDetalle(prev => [...prev, {
    ...EMPTY_LINEA,
    porcentaje_descuento: parseFloat(form.descuento_porcentaje) || 0,
    id_centro_costo: form?.id_centro_costo || ccUnicoId || null,
  }]);
  const eliminarLinea = (idx) => setDetalle(prev => prev.filter((_, i) => i !== idx));

  const applyFmtObs = (type) => {
    const el = document.getElementById('oc-obs-main');
    const obs = form.observaciones || '';
    const start = el ? el.selectionStart : obs.length;
    const end   = el ? el.selectionEnd   : obs.length;
    const sel   = obs.substring(start, end);
    const [pre, suf] = type === 'bold' ? ['*', '*'] : ['#', '#'];
    const newVal = obs.substring(0, start) + pre + sel + suf + obs.substring(end);
    setForm(prev => ({ ...prev, observaciones: newVal }));
    setTimeout(() => { if (el) { el.setSelectionRange(start + 1, end + 1); el.focus(); } }, 0);
  };

  const applyFmt = (idx, obs, type) => {
    const el = document.getElementById(`oc-obs-${idx}`);
    const start = el ? el.selectionStart : obs.length;
    const end   = el ? el.selectionEnd   : obs.length;
    const sel   = obs.substring(start, end);
    const [pre, suf] = type === 'bold' ? ['**', '**'] : ['[[', ']]'];
    const newVal = obs.substring(0, start) + pre + sel + suf + obs.substring(end);
    updateLinea(idx, 'observacion', newVal);
    setTimeout(() => { if (el) { el.setSelectionRange(start + 2, end + 2); el.focus(); } }, 0);
  };
  const updateLinea = (idx, field, value) => {
    setDetalle(prev => {
      const next = [...prev];
      const linea = { ...next[idx], [field]: value };
      if (field === 'precio_soles') {
        const tc = parseFloat(form.tipo_cambio) || 1;
        linea.precio_dolares = parseFloat((parseFloat(value) / tc).toFixed(4));
      } else if (field === 'precio_dolares') {
        const tc = parseFloat(form.tipo_cambio) || 1;
        linea.precio_soles = parseFloat((parseFloat(value) * tc).toFixed(4));
      }
      next[idx] = calcLinea(linea, form.tipo_cambio, form.descuento_porcentaje);
      return next;
    });
  };
  const setArticuloLinea = (idx, articulo) => {
    if (!articulo) {
      setDetalle(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], id_articulo: null, articulo_codigo: '', articulo_descripcion: '', unidad_descripcion: '' };
        return next;
      });
      return;
    }
    setDetalle(prev => {
      const next = [...prev];
      next[idx] = calcLinea({
        ...next[idx],
        id_articulo: articulo.id_articulo,
        articulo_codigo: articulo.codigo_interno || '',
        articulo_descripcion: articulo.descripcion || '',
        porcentaje_igv: articulo.porcentaje_igv_compra ?? 18,
        unidad_descripcion: articulo.unidad_descripcion || '',
      }, form.tipo_cambio, form.descuento_porcentaje);
      return next;
    });
  };
  const aplicarDescuentoCab = () => {
    const pct = parseFloat(form.descuento_porcentaje) || 0;
    setDetalle(prev => prev.map(l => calcLinea({ ...l, porcentaje_descuento: pct }, form.tipo_cambio, pct)));
  };

  const toggleRevisado = async (idx, linea) => {
    const nuevoVal = !linea.revisado;
    setDetalle(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], revisado: nuevoVal };
      return next;
    });
    if (linea.id_detalle && editing) {
      try { await ordenCompraService.toggleRevisado(linea.id_detalle, nuevoVal); } catch {}
    }
  };

  // ── Abrir formulario ──
  const openCreate = () => {
    setEditing(null);
    setForm(makeEmptyForm());
    setDetalle([]);
    setTcSource('');
    setView('form');
  };

  const openEdit = async (r) => {
    try {
      const res = await ordenCompraService.getById(r.id_orden);
      const oc = res.data;
      setEditing(oc);
      setForm({
        id_sucursal: oc.id_sucursal,
        sucursal_nombre: oc.sucursal_nombre || '',
        tipo_documento: oc.tipo_documento || 'OC',
        id_serie: oc.id_serie || '',
        numero: oc.numero || '',
        tipo_oc: oc.tipo_oc || 'NACIONAL',
        fecha_emision: oc.fecha_emision?.split('T')[0] || today(),
        fecha_entrega: oc.fecha_entrega?.split('T')[0] || today(),
        moneda: oc.moneda || 'PEN',
        tipo_cambio: oc.tipo_cambio || 1,
        id_proveedor: oc.id_proveedor || null,
        proveedor_nombre: oc.proveedor_nombre || '',
        proveedor_ruc: oc.proveedor_ruc || '',
        proveedor_direccion: oc.proveedor_direccion || '',
        id_forma_pago: oc.id_forma_pago || null,
        id_solicitante: oc.id_solicitante || null,
        descuento_porcentaje: oc.descuento_porcentaje || 0,
        id_almacen_entrega: oc.id_almacen_entrega || null,
        id_tipo_doc_ref1: oc.id_tipo_doc_ref1 || null,
        num_doc_ref1: oc.num_doc_ref1 || '',
        id_tipo_doc_ref2: oc.id_tipo_doc_ref2 || null,
        num_doc_ref2: oc.num_doc_ref2 || '',
        id_persona_atencion: oc.id_persona_atencion || null,
        id_almacen_factura: oc.id_almacen_factura || null,
        observaciones: oc.observaciones || '',
        id_centro_costo: (() => {
          const ids = new Set((oc.detalle || []).map(d => d.id_centro_costo || null));
          return ids.size === 1 ? [...ids][0] || null : null;
        })(),
      });
      setDetalle((oc.detalle || []).map(d => ({
        id_detalle: d.id_detalle,
        id_articulo: d.id_articulo,
        articulo_descripcion: d.articulo_descripcion,
        articulo_codigo: d.articulo_codigo,
        cantidad: d.cantidad,
        precio_soles: d.precio_soles,
        precio_dolares: d.precio_dolares,
        porcentaje_igv: d.porcentaje_igv ?? 18,
        igv_soles: d.igv_soles, igv_dolares: d.igv_dolares,
        total_soles: d.total_soles, total_dolares: d.total_dolares,
        porcentaje_descuento: d.porcentaje_descuento ?? 0,
        descuento_soles: d.descuento_soles, descuento_dolares: d.descuento_dolares,
        total_con_descuento_soles: d.total_con_descuento_soles,
        total_con_descuento_dolares: d.total_con_descuento_dolares,
        observacion: d.observacion || '',
        unidad_descripcion: (() => { const a = articulos.find(x => x.id_articulo === d.id_articulo); return a?.unidad_descripcion || ''; })(),
        id_requerimiento: d.id_requerimiento || null,
        id_requerimiento_detalle: d.id_requerimiento_detalle || null,
        rq_serie: d.rq_serie || '',
        rq_numero: d.rq_numero || '',
        revisado: !!d.revisado,
        id_centro_costo: d.id_centro_costo || null,
        centro_costo_codigo: d.centro_costo_codigo || '',
        centro_costo_descripcion: d.centro_costo_descripcion || '',
      })));
      setTcSource('db');
      setView('form');
    } catch { setAlert({ type: 'error', message: 'Error al cargar la orden de compra.' }); }
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (saving) return;
    if (!form.id_serie)        { setAlert({ type: 'warning', message: 'La serie es obligatoria.' }); return; }
    if (!form.numero)          { setAlert({ type: 'warning', message: 'El número es obligatorio.' }); return; }
    if (!form.fecha_emision)   { setAlert({ type: 'warning', message: 'La fecha de emisión es obligatoria.' }); return; }
    if (!form.moneda)          { setAlert({ type: 'warning', message: 'La moneda es obligatoria.' }); return; }
    if (!form.tipo_cambio || parseFloat(form.tipo_cambio) <= 0) {
      setAlert({ type: 'warning', message: 'Debe ingresar el tipo de cambio.' }); return;
    }
    if (!form.id_proveedor)    { setAlert({ type: 'warning', message: 'El proveedor es obligatorio.' }); return; }
    if (!form.id_forma_pago)   { setAlert({ type: 'warning', message: 'La forma de pago es obligatoria.' }); return; }
    if (!form.id_tipo_doc_ref1){ setAlert({ type: 'warning', message: 'El tipo de documento de referencia 1 es obligatorio.' }); return; }
    if (!form.num_doc_ref1)    { setAlert({ type: 'warning', message: 'El número de documento de referencia 1 es obligatorio.' }); return; }
    if (detalle.length === 0)  { setAlert({ type: 'warning', message: 'Debe agregar al menos un artículo al detalle.' }); return; }
    const lineaSinPrecio = detalle.findIndex(l => !l.id_articulo ? false : (parseFloat(l.precio_soles) <= 0 && parseFloat(l.precio_dolares) <= 0));
    if (lineaSinPrecio !== -1) {
      setAlert({ type: 'warning', message: `La línea ${lineaSinPrecio + 1} tiene precio cero. Todos los artículos deben tener precio.` }); return;
    }
    const lineaSinCC = detalle.findIndex(l => !l.id_centro_costo);
    if (lineaSinCC !== -1) {
      setAlert({ type: 'warning', message: `La línea ${lineaSinCC + 1} requiere centro de costo.` }); return;
    }
    const payload = {
      numero: form.numero,
      id_sucursal: form.id_sucursal || null,
      tipo_documento: 'OC',
      id_serie: form.id_serie ? parseInt(form.id_serie) : null,
      tipo_oc: form.tipo_oc || null,
      fecha_emision: form.fecha_emision,
      fecha_entrega: form.fecha_entrega || null,
      moneda: form.moneda,
      tipo_cambio: parseFloat(form.tipo_cambio) || 1,
      id_proveedor: form.id_proveedor || null,
      id_forma_pago: form.id_forma_pago || null,
      id_solicitante: form.id_solicitante || null,
      descuento_porcentaje: parseFloat(form.descuento_porcentaje) || 0,
      id_almacen_entrega: form.id_almacen_entrega || null,
      id_tipo_doc_ref1: form.id_tipo_doc_ref1 || null,
      num_doc_ref1: form.num_doc_ref1 || null,
      id_tipo_doc_ref2: form.id_tipo_doc_ref2 || null,
      num_doc_ref2: form.num_doc_ref2 || null,
      id_persona_atencion: form.id_persona_atencion || null,
      id_almacen_factura: form.id_almacen_factura || null,
      observaciones: form.observaciones || null,
      ...totales,
      detalle: detalle.map((l, i) => ({ ...l, orden: i + 1 })),
    };
    setSaving(true);
    try {
      if (editing) {
        await ordenCompraService.update(editing.id_orden, payload);
      } else {
        await ordenCompraService.create(payload);
      }
      setView('list');
      fetchAll();
      setToast(editing ? 'Orden de compra actualizada' : 'Orden de compra creada');
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    } finally {
      setSaving(false);
    }
  };

  // ── Modal Proveedor: handlers ──
  const handleConsultarRuc = async () => {
    if (!formProv.ruc || formProv.ruc.length !== 11) {
      setAlert({ type: 'warning', message: 'Ingrese un RUC válido de 11 dígitos.' }); return;
    }
    setConsultandoRuc(true);
    try {
      const res = await entidadService.consultarRuc(formProv.ruc);
      setFormProv(p => ({ ...p, razon_social: res.data.razon_social || p.razon_social, direccion: res.data.direccion || p.direccion }));
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'RUC no encontrado.' });
    } finally { setConsultandoRuc(false); }
  };

  const handleSaveProv = async (e) => {
    e.preventDefault();
    if (!formProv.razon_social) { setAlert({ type: 'warning', message: 'Razón Social es obligatoria.' }); return; }
    setSavingProv(true);
    try {
      const data = {};
      Object.entries(formProv).forEach(([k, v]) => { data[k] = v === '' ? null : v; });
      const res = await entidadService.create(data);
      const nuevo = res.data;
      setEntidades(prev => [...prev, nuevo]);
      setForm(prev => ({
        ...prev,
        id_proveedor: nuevo.id_entidad,
        proveedor_nombre: nuevo.razon_social || '',
        proveedor_ruc: nuevo.ruc || '',
        proveedor_direccion: nuevo.direccion || '',
      }));
      setModalProv(false);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar proveedor.') });
    } finally { setSavingProv(false); }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Orden de Compra', message: `¿Eliminar la orden "${r.numero}"?`,
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        setConfirm(null);
        try { await ordenCompraService.delete(r.id_orden); setToast('Orden eliminada'); fetchAll(); }
        catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar.') }); }
      },
    });
  };

  const handleAprobar = (r) => {
    setConfirm({
      title: 'Aprobar Orden de Compra',
      message: `¿Aprobar la orden "${r.numero}"?`,
      confirmLabel: 'Aprobar',
      confirmStyle: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.3)' },
      onConfirm: async () => {
        setConfirm(null);
        try {
          await ordenCompraService.aprobar(r.id_orden, { estado: 'APROBADO' });
          setToast('Orden aprobada'); fetchAll();
        } catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al aprobar.') }); }
      },
    });
  };

  const handleDesaprobar = (r) => {
    setConfirm({
      title: 'Desaprobar Orden de Compra',
      message: `¿Desaprobar la orden "${r.numero}"?`,
      confirmLabel: 'Desaprobar',
      confirmStyle: { background: 'rgba(251,146,60,0.15)', color: '#fb923c', borderColor: 'rgba(251,146,60,0.3)' },
      onConfirm: async () => {
        setConfirm(null);
        try {
          await ordenCompraService.aprobar(r.id_orden, { estado: 'DESAPROBADO' });
          setToast('Orden desaprobada'); fetchAll();
        } catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al desaprobar.') }); }
      },
    });
  };

  const handleVolverPendiente = (r) => {
    setConfirm({
      title: 'Volver a Pendiente',
      message: `¿Devolver la orden "${r.numero}" a estado Pendiente?`,
      confirmLabel: 'Confirmar',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await ordenCompraService.aprobar(r.id_orden, { estado: 'PENDIENTE' });
          setToast('Orden vuelta a Pendiente'); fetchAll();
        } catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cambiar estado.') }); }
      },
    });
  };

  const handleAnular = (r) => {
    setConfirm({
      title: 'Anular Orden de Compra',
      message: `¿Anular la orden "${r.numero}"? Esta acción no se puede revertir.`,
      confirmLabel: 'Anular',
      confirmStyle: { background: 'rgba(248,113,113,0.15)', color: '#f87171', borderColor: 'rgba(248,113,113,0.3)' },
      onConfirm: async () => {
        setConfirm(null);
        try {
          await ordenCompraService.aprobar(r.id_orden, { estado: 'ANULADO' });
          setToast('Orden anulada'); fetchAll();
        } catch (err) { setAlert({ type: 'error', message: getErrorDetail(err, 'Error al anular.') }); }
      },
    });
  };

  const handleDuplicar = async (r) => {
    try {
      const res = await ordenCompraService.getById(r.id_orden);
      const oc = res.data;
      const tc = parseFloat(oc.tipo_cambio) || 1;
      const descPct = parseFloat(oc.descuento_porcentaje) || 0;
      setEditing(null);
      setForm({
        id_sucursal: sucursal?.id_sucursal || oc.id_sucursal,
        sucursal_nombre: sucursal?.nombre || oc.sucursal_nombre || '',
        tipo_documento: 'OC',
        id_serie: oc.id_serie || null,
        numero: '',
        tipo_oc: oc.tipo_oc || 'NACIONAL',
        fecha_emision: today(), fecha_entrega: today(),
        moneda: oc.moneda || 'PEN',
        tipo_cambio: tc,
        id_proveedor: oc.id_proveedor || null,
        proveedor_nombre: oc.proveedor_nombre || '',
        proveedor_ruc: oc.proveedor_ruc || '',
        proveedor_direccion: oc.proveedor_direccion || '',
        id_forma_pago: oc.id_forma_pago || null,
        id_solicitante: oc.id_solicitante || null,
        descuento_porcentaje: descPct,
        id_almacen_entrega: oc.id_almacen_entrega || null,
        id_tipo_doc_ref1: null, num_doc_ref1: '',
        id_tipo_doc_ref2: null, num_doc_ref2: '',
        id_persona_atencion: oc.id_persona_atencion || null,
        id_almacen_factura: oc.id_almacen_factura || null,
        observaciones: oc.observaciones || '',
        id_centro_costo: (() => {
          const ids = new Set((oc.detalle || []).map(d => d.id_centro_costo || null));
          return ids.size === 1 ? [...ids][0] || null : null;
        })(),
      });
      // Recalcular cada línea con calcLinea para garantizar totales correctos
      setDetalle((oc.detalle || []).map(d => calcLinea({
        id_articulo: d.id_articulo,
        articulo_descripcion: d.articulo_descripcion,
        articulo_codigo: d.articulo_codigo,
        cantidad: d.cantidad,
        precio_soles: d.precio_soles,
        precio_dolares: d.precio_dolares,
        porcentaje_igv: d.porcentaje_igv ?? 18,
        porcentaje_descuento: d.porcentaje_descuento ?? 0,
        observacion: d.observacion || '',
        unidad_descripcion: articulos.find(x => x.id_articulo === d.id_articulo)?.unidad_descripcion || d.unidad_descripcion || '',
        id_centro_costo: d.id_centro_costo || null,
        centro_costo_codigo: d.centro_costo_codigo || '',
        centro_costo_descripcion: d.centro_costo_descripcion || '',
      }, tc, descPct)));
      setTcSource('db');
      setView('form');
      // Generar el siguiente número para la serie copiada
      if (oc.id_serie) generarNumero(oc.id_serie);
    } catch { setAlert({ type: 'error', message: 'Error al cargar la orden para duplicar.' }); }
  };

  const doPrint = async (r) => {
    try {
      const [detRes, empRes] = await Promise.allSettled([
        ordenCompraService.getById(r.id_orden),
        datosGeneralesService.get(),
      ]);
      const oc = detRes.status === 'fulfilled' ? detRes.value.data : r;
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = oc.detalle || [];
      const fmt = (d) => d ? d.split('T')[0] : '—';
      const fmtN = (v, dec = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
      const formatObs = (text) => {
        if (!text) return '';
        return text
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
          .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
          .replace(/\[\[([^\]\n]+)\]\]/g, '<span style="background:#2563eb;color:#fff;border-radius:3px;padding:0 4px;font-weight:600">$1</span>')
          .replace(/#([^#\n]+)#/g, '<span style="background:#2563eb;color:#fff;border-radius:3px;padding:0 4px;font-weight:600">$1</span>');
      };
      const esPEN = oc.moneda === 'PEN';
      const sym = esPEN ? 'S/' : 'US$';
      const fp = formasPago.find(x => x.id_forma_pago === oc.id_forma_pago);
      const baseImp = esPEN ? oc.base_imponible_soles : oc.base_imponible_dolares;
      const igvT   = esPEN ? oc.igv_soles : oc.igv_dolares;
      const noGrav = esPEN ? oc.no_gravado_soles : oc.no_gravado_dolares;
      const desc   = esPEN ? oc.descuento_soles : oc.descuento_dolares;
      const total  = esPEN ? oc.total_soles : oc.total_dolares;
      const ECOL = { PENDIENTE: '#b45309', APROBADO: '#166534', DESAPROBADO: '#c2410c', ANULADO: '#475569' };
      const EBG  = { PENDIENTE: '#fef3c7', APROBADO: '#dcfce7', DESAPROBADO: '#ffedd5', ANULADO: '#f1f5f9' };
      // Centros de costo: decidir si cabecera única o lista agrupada + columna por línea
      const ccLabelPorLinea = (d) => d.centro_costo_codigo
        ? `${d.centro_costo_codigo}${d.centro_costo_descripcion ? ' — ' + d.centro_costo_descripcion : ''}`
        : '—';
      const ccUnicos = (() => {
        const mapa = new Map();
        det.forEach(d => {
          const key = d.id_centro_costo || null;
          if (key && !mapa.has(key)) mapa.set(key, ccLabelPorLinea(d));
        });
        return mapa;
      })();
      const ccTodosIguales = det.length > 0 && ccUnicos.size === 1 && det.every(d => d.id_centro_costo);
      const ccCabeceraLabel = ccTodosIguales
        ? [...ccUnicos.values()][0]
        : [...ccUnicos.values()].join(', ');
      const mostrarColumnaCC = !ccTodosIguales && ccUnicos.size > 0;

      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/>
<title>Orden de Compra ${oc.numero || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10.5px; color: #1e293b; background: #fff; padding: 20px 28px 24px; }
  .top-stripe { height: 5px; background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%); border-radius: 2px; margin-bottom: 16px; }
  .header { display: flex; justify-content: space-between; align-items: center; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo-box { width: 52px; height: 52px; background: #1a3a6b; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .logo-box svg { fill: #fff; }
  .logo-img { max-height: 56px; max-width: 130px; object-fit: contain; }
  .emp-name { font-size: 14px; font-weight: 700; color: #1a3a6b; line-height: 1.2; }
  .emp-ruc  { font-size: 10px; font-weight: 600; color: #475569; margin-top: 2px; }
  .emp-sub  { font-size: 9px; color: #64748b; margin-top: 2px; line-height: 1.5; }
  .doc-box { border: 2px solid #1a3a6b; border-radius: 8px; padding: 9px 18px; text-align: center; min-width: 180px; background: #f5f8ff; }
  .doc-box-label { font-size: 7.5px; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 1.2px; }
  .doc-box-title { font-size: 13px; font-weight: 800; color: #1a3a6b; margin: 3px 0 2px; }
  .doc-box-num { font-size: 16px; font-weight: 700; color: #1a3a6b; }
  .divider { border: none; border-top: 2.5px solid #1a3a6b; margin: 10px 0 8px; }
  .section-header { background: #1a3a6b; color: #fff; font-size: 7.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 3px 8px; margin: 6px 0 3px; border-radius: 3px; }
  .grid-6 { display: grid; grid-template-columns: repeat(6, 1fr); gap: 3px 6px; margin-bottom: 3px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3px 6px; margin-bottom: 3px; }
  .grid-1 { margin-bottom: 3px; }
  .span-2 { grid-column: span 2; }
  .span-3 { grid-column: span 3; }
  .field { border: 1px solid #c8dcf5; border-radius: 3px; padding: 2px 6px; background: transparent; }
  .field-label { font-size: 6.8px; font-weight: 700; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.6px; line-height: 1.2; }
  .field-value { font-size: 10px; color: #1e293b; font-weight: 500; min-height: 12px; line-height: 1.25; margin-top: 0; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  thead tr { background: #1a3a6b; }
  th { font-size: 8px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; padding: 5px 7px; text-align: left; border: 1px solid #1a3a6b; }
  th.right { text-align: right; }
  th.center { text-align: center; }
  td { padding: 4px 7px; border: 1px solid #dce8f8; font-size: 9.5px; vertical-align: middle; color: #1e293b; }
  td.right { text-align: right; font-variant-numeric: tabular-nums; }
  td.center { text-align: center; }
  tr:nth-child(even) td { background: #f5f8ff; }
  tr:nth-child(odd)  td { background: #fff; }
  .totals-table { width: auto; margin-left: auto; min-width: 280px; margin-top: 8px; border-collapse: collapse; }
  .totals-table td { font-size: 10px; padding: 4px 10px; border: 1px solid #dce8f8; }
  .totals-table td:first-child { text-align: right; color: #475569; font-weight: 500; background: #f5f8ff; }
  .totals-table td:last-child { text-align: right; font-weight: 600; color: #1e293b; min-width: 110px; }
  .totals-table tr.total-main td { font-size: 12px; font-weight: 800; color: #1a3a6b; background: #e8f0fb !important; border-top: 2px solid #a8c4e8; }
  .page-footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid #dce8f8; display: flex; justify-content: space-between; font-size: 8px; color: #94a3b8; }
  @media print {
    body { padding: 10px 16px 16px; }
    @page { margin: 8mm 10mm; size: A4; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .top-stripe { background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%) !important; }
    .section-header { background: #1a3a6b !important; color: #fff !important; }
    thead tr { background: #1a3a6b !important; }
    th { color: #fff !important; background: #1a3a6b !important; }
    .field { background: transparent !important; }
    tr:nth-child(even) td { background: #f5f8ff !important; }
    .totals-table tr.total-main td { background: #e8f0fb !important; }
    .totals-table td:first-child { background: #f5f8ff !important; }
    .doc-box { background: #f5f8ff !important; }
    .logo-box { background: #1a3a6b !important; }
  }
</style>
</head><body>
<div class="top-stripe"></div>
<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo" />`
      : `<div class="logo-box"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></div>`
    }
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${emp.direccion || ''}${emp.telefono ? ' &nbsp;·&nbsp; Tel: ' + emp.telefono : ''}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">ORDEN DE COMPRA</div>
    <div class="doc-box-num">${oc.serie_codigo ? oc.serie_codigo.trim() + '-' : ''}${oc.numero || '—'}</div>
  </div>
</div>
<hr class="divider"/>
<div class="section-header">Información General</div>
<div class="grid-6">
  <div class="field"><div class="field-label">Fecha Emisión</div><div class="field-value">${fmt(oc.fecha_emision)}</div></div>
  <div class="field"><div class="field-label">Fecha Entrega</div><div class="field-value">${fmt(oc.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${oc.sucursal_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Moneda</div><div class="field-value">${oc.moneda === 'USD' ? 'USD' : 'PEN'}${oc.moneda === 'USD' && parseFloat(oc.tipo_cambio) ? ` · TC ${fmtN(oc.tipo_cambio, 4)}` : ''}</div></div>
  <div class="field"><div class="field-label">Tipo OC</div><div class="field-value">${oc.tipo_oc || '—'}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${EBG[oc.estado]||'#f1f5f9'};color:${ECOL[oc.estado]||'#475569'}">${oc.estado || '—'}</span>
  </div></div>
</div>
<div class="section-header">Proveedor</div>
<div class="grid-4">
  <div class="field span-2"><div class="field-label">Razón Social</div><div class="field-value">${oc.proveedor_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">RUC</div><div class="field-value">${oc.proveedor_ruc || '—'}</div></div>
  <div class="field"><div class="field-label">Contacto</div><div class="field-value">${oc.proveedor_contacto || '—'}</div></div>
</div>
<div class="grid-4">
  <div class="field span-2"><div class="field-label">Dirección</div><div class="field-value">${oc.proveedor_direccion || '—'}</div></div>
  <div class="field"><div class="field-label">Teléfono</div><div class="field-value">${oc.proveedor_telefono || oc.proveedor_celular || '—'}</div></div>
  <div class="field"><div class="field-label">Correo</div><div class="field-value">${oc.proveedor_correo || '—'}</div></div>
</div>
<div class="section-header">Entrega y Pago</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Forma de Pago</div><div class="field-value">${fp?.descripcion || oc.forma_pago_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Lugar de Entrega</div><div class="field-value">${oc.almacen_entrega_nombre || '—'}</div></div>
  <div class="field span-2"><div class="field-label">Dirección de Entrega</div><div class="field-value">${oc.almacen_entrega_direccion || '—'}</div></div>
</div>
${ccCabeceraLabel ? `<div class="grid-1"><div class="field"><div class="field-label">Centro${ccTodosIguales ? '' : 's'} de Costo</div><div class="field-value">${ccCabeceraLabel}</div></div></div>` : ''}
<div class="section-header">Detalle de Artículos</div>
<table>
  <thead>
    <tr>
      <th style="width:24px" class="center">#</th>
      <th style="width:80px">Código</th>
      <th>Descripción</th>
      <th style="width:56px">Unidad</th>
      ${mostrarColumnaCC ? '<th style="width:70px">C. Costo</th>' : ''}
      <th style="width:50px" class="center">Cant.</th>
      <th style="width:82px" class="right">Precio</th>
      <th style="width:36px" class="center">%IGV</th>
      <th style="width:82px" class="right">IGV Unit.</th>
      <th style="width:92px" class="right">Total Línea</th>
    </tr>
  </thead>
  <tbody>
    ${det.map((d, i) => {
      const precio = esPEN ? d.precio_soles : d.precio_dolares;
      const igvU   = esPEN ? d.igv_soles   : d.igv_dolares;
      const totalL = esPEN ? d.total_con_descuento_soles : d.total_con_descuento_dolares;
      const uDesc  = articulos.find(x => x.id_articulo === d.id_articulo)?.unidad_descripcion || d.unidad_descripcion || '—';
      return `<tr>
        <td class="center" style="color:#94a3b8">${i + 1}</td>
        <td style="font-family:monospace;font-size:9px">${d.articulo_codigo || '—'}</td>
        <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
        <td style="font-size:9px;color:#475569">${uDesc}</td>
        ${mostrarColumnaCC ? `<td style="font-size:9px;color:#475569;font-family:monospace">${d.centro_costo_codigo || '—'}</td>` : ''}
        <td class="center" style="font-weight:700">${(parseFloat(d.cantidad)||0).toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
        <td class="right">${sym} ${fmtN(precio,4)}</td>
        <td class="center">${d.porcentaje_igv ?? 18}%</td>
        <td class="right">${sym} ${fmtN(igvU,4)}</td>
        <td class="right" style="font-weight:700">${sym} ${fmtN(totalL,4)}</td>
      </tr>`;
    }).join('')}
    ${det.length === 0 ? `<tr><td colspan="${mostrarColumnaCC ? 10 : 9}" class="center" style="color:#94a3b8;padding:12px">Sin artículos registrados</td></tr>` : ''}
  </tbody>
</table>
<table class="totals-table">
  <tbody>
    ${parseFloat(baseImp) > 0 ? `<tr><td>Base Imponible:</td><td>${sym} ${fmtN(baseImp,4)}</td></tr>` : ''}
    ${parseFloat(igvT)   > 0 ? `<tr><td>IGV:</td><td>${sym} ${fmtN(igvT,4)}</td></tr>` : ''}
    ${parseFloat(noGrav) > 0 ? `<tr><td>No Gravados:</td><td>${sym} ${fmtN(noGrav,4)}</td></tr>` : ''}
    ${parseFloat(desc)   > 0 ? `<tr><td>Descuentos:</td><td>− ${sym} ${fmtN(desc,4)}</td></tr>` : ''}
    <tr class="total-main"><td>TOTAL A PAGAR:</td><td>${sym} ${fmtN(total,2)}</td></tr>
    ${!esPEN ? `<tr><td style="color:#64748b">Equivalente en soles:</td><td style="color:#64748b">S/ ${fmtN(oc.total_soles,2)}</td></tr>` : ''}
  </tbody>
</table>
${oc.observaciones ? `<div class="grid-1" style="margin-top:10px"><div class="field"><div class="field-label">Observaciones</div><div class="field-value" style="line-height:1.6">${formatObs(oc.observaciones)}</div></div></div>` : ''}
${oc.usuario_aprobacion || oc.estado === 'APROBADO' ? `
<div class="section-header">Aprobación</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Aprobado por</div><div class="field-value">${oc.usuario_aprobacion || '—'}</div></div>
  <div class="field"><div class="field-label">Fecha</div><div class="field-value">${oc.fecha_aprobacion ? new Date(oc.fecha_aprobacion).toLocaleString('es-PE',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</div></div>
  ${oc.observacion_aprobacion ? `<div class="field"><div class="field-label">Observación</div><div class="field-value">${oc.observacion_aprobacion}</div></div>` : '<div></div>'}
</div>` : ''}
<div class="page-footer">
  <span>${emp.razon_social || ''} — RUC: ${emp.ruc_empresa || ''}</span>
  <span>Impreso: ${new Date().toLocaleDateString('es-PE',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
</div>
</body></html>`;

      const w = window.open('', '_blank', 'width=950,height=780');
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 450);
    } catch { setAlert({ type: 'error', message: 'Error al generar la impresión.' }); }
  };

  // ── Modal Ver OC: carga detalle completo ──────────────────────────
  const abrirViewOC = async (r) => {
    setViewModal(r);
    setViewData(null);
    setLoadingView(true);
    try {
      const res = await ordenCompraService.getById(r.id_orden);
      setViewData(res.data);
    } catch { setAlert({ type: 'error', message: 'Error al cargar la orden de compra.' }); setViewModal(null); }
    finally { setLoadingView(false); }
  };

  // ── Modal Origen: abre y carga RQs únicos del detalle de la OC ──
  const abrirOrigenModal = async (row) => {
    setOrigenModal(row);
    setOrigenRQs([]);
    setLoadingOrigen(true);
    try {
      const res = await ordenCompraService.getById(row.id_orden);
      const det = res.data?.detalle || [];
      const seen = new Set();
      const rqs = [];
      det.forEach(d => {
        if (d.id_requerimiento && !seen.has(d.id_requerimiento)) {
          seen.add(d.id_requerimiento);
          rqs.push({ id_rq: d.id_requerimiento, numero: d.rq_numero, serie: d.rq_serie });
        }
      });
      setOrigenRQs(rqs);
    } catch { setAlert({ type: 'error', message: 'Error al cargar requerimientos del origen.' }); setOrigenModal(null); }
    finally { setLoadingOrigen(false); }
  };

  // ── Modal Ingreso PE: abre y carga partes de entrada de la OC ──
  const abrirPeModal = async (row) => {
    setPeModal(row);
    setPeAtencionData([]);
    setLoadingPe(true);
    try {
      const res = await parteEntradaService.getAtencionOc(row.id_orden);
      setPeAtencionData(res.data || []);
    } catch { setAlert({ type: 'error', message: 'Error al cargar partes de entrada.' }); setPeModal(null); }
    finally { setLoadingPe(false); }
  };

  // ── Preview RQ en nueva pestaña (sin auto-imprimir) ──
  const previewRQ = async (id_rq) => {
    try {
      const [detRes, empRes] = await Promise.allSettled([
        requerimientoService.getById(id_rq),
        datosGeneralesService.get(),
      ]);
      const req = detRes.status === 'fulfilled' ? detRes.value.data : {};
      const emp = empRes.status === 'fulfilled' ? empRes.value.data : {};
      const det = req.detalle || [];
      const fmt = (d) => d ? d.split('T')[0] : '—';
      const estColor = { PENDIENTE: '#b45309', APROBADO: '#166534', RECHAZADO: '#991b1b' };
      const estBg    = { PENDIENTE: '#fef3c7', APROBADO: '#dcfce7', RECHAZADO: '#fee2e2' };
      const html = `<!DOCTYPE html><html lang="es"><head>
<meta charset="UTF-8"/><title>Requerimiento ${req.numero || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 10.5px; color: #1e293b; background: #fff; padding: 20px 28px 24px; }
  .top-stripe { height: 5px; background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%); border-radius: 2px; margin-bottom: 16px; }
  .header { display: flex; justify-content: space-between; align-items: center; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .logo-box { width: 52px; height: 52px; background: #1a3a6b; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
  .logo-box svg { fill: #fff; }
  .logo-img { max-height: 56px; max-width: 130px; object-fit: contain; }
  .emp-name { font-size: 14px; font-weight: 700; color: #1a3a6b; }
  .emp-ruc  { font-size: 10px; font-weight: 600; color: #475569; margin-top: 2px; }
  .emp-sub  { font-size: 9px; color: #64748b; margin-top: 2px; line-height: 1.5; }
  .doc-box { border: 2px solid #1a3a6b; border-radius: 8px; padding: 9px 18px; text-align: center; min-width: 165px; background: #f5f8ff; }
  .doc-box-label { font-size: 7.5px; font-weight: 700; color: #1a3a6b; text-transform: uppercase; letter-spacing: 1.2px; }
  .doc-box-title { font-size: 13px; font-weight: 800; color: #1a3a6b; margin: 3px 0 2px; }
  .doc-box-num { font-size: 16px; font-weight: 700; color: #1a3a6b; }
  .divider { border: none; border-top: 2.5px solid #1a3a6b; margin: 12px 0 14px; }
  .section-header { background: #1a3a6b; color: #fff; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; padding: 4px 10px; margin: 12px 0 7px; border-radius: 3px; }
  .grid-4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 10px; margin-bottom: 5px; }
  .grid-1 { margin-bottom: 5px; }
  .field { border: 1px solid #c8dcf5; border-radius: 3px; padding: 4px 7px; background: #f5f8ff; }
  .field-label { font-size: 7.5px; font-weight: 700; color: #7a9cc4; text-transform: uppercase; letter-spacing: 0.8px; }
  .field-value { font-size: 10.5px; color: #1e293b; font-weight: 500; min-height: 13px; margin-top: 1px; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 9px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 2px; }
  thead tr { background: #1a3a6b; }
  th { font-size: 8.5px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 8px; text-align: left; border: 1px solid #1a3a6b; }
  th.center { text-align: center; }
  td { padding: 5px 8px; border: 1px solid #dce8f8; font-size: 10px; vertical-align: middle; color: #1e293b; }
  td.center { text-align: center; }
  tr:nth-child(even) td { background: #f5f8ff; }
  tr:nth-child(odd)  td { background: #fff; }
  tfoot td { background: #e8f0fb !important; font-weight: 700; font-size: 9.5px; color: #1a3a6b; border-top: 2px solid #a8c4e8; }
  .page-footer { margin-top: 22px; padding-top: 8px; border-top: 1px solid #dce8f8; display: flex; justify-content: space-between; font-size: 8px; color: #94a3b8; }
  @media print {
    body { padding: 10px 16px 16px; }
    @page { margin: 8mm 10mm; size: A4; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .top-stripe { background: linear-gradient(90deg, #1a3a6b 0%, #2563eb 100%) !important; }
    .section-header { background: #1a3a6b !important; color: #fff !important; }
    thead tr { background: #1a3a6b !important; }
    th { color: #fff !important; background: #1a3a6b !important; }
    .field { background: #f5f8ff !important; }
    tr:nth-child(even) td { background: #f5f8ff !important; }
    tfoot td { background: #e8f0fb !important; }
    .doc-box { background: #f5f8ff !important; }
    .logo-box { background: #1a3a6b !important; }
  }
</style></head><body>
<div class="top-stripe"></div>
<div class="header">
  <div class="header-left">
    ${emp.logo_base64
      ? `<img src="${emp.logo_base64}" class="logo-img" alt="Logo" />`
      : `<div class="logo-box"><svg viewBox="0 0 24 24" width="28" height="28"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg></div>`}
    <div>
      <div class="emp-name">${emp.razon_social || 'Empresa'}</div>
      <div class="emp-ruc">RUC: ${emp.ruc_empresa || '—'}</div>
      <div class="emp-sub">${emp.direccion || ''}${emp.telefono ? ' · Tel: ' + emp.telefono : ''}</div>
    </div>
  </div>
  <div class="doc-box">
    <div class="doc-box-label">Documento</div>
    <div class="doc-box-title">REQUERIMIENTO</div>
    <div class="doc-box-num">${req.serie_codigo ? req.serie_codigo.trim() + '-' : ''}${req.numero || '—'}</div>
  </div>
</div>
<hr class="divider"/>
<div class="section-header">Información General</div>
<div class="grid-4">
  <div class="field"><div class="field-label">Fecha Emisión</div><div class="field-value">${fmt(req.fecha_emision)}</div></div>
  <div class="field"><div class="field-label">Fecha Entrega</div><div class="field-value">${fmt(req.fecha_entrega)}</div></div>
  <div class="field"><div class="field-label">Prioridad</div><div class="field-value">${req.prioridad_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Estado</div><div class="field-value">
    <span class="badge" style="background:${estBg[req.estado]||'#f1f5f9'};color:${estColor[req.estado]||'#475569'}">${req.estado || '—'}</span>
  </div></div>
</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Sucursal</div><div class="field-value">${req.sucursal_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Área</div><div class="field-value">${req.area_descripcion || '—'}</div></div>
  <div class="field"><div class="field-label">Centro de Costo</div><div class="field-value">${req.centro_costo_descripcion || '—'}</div></div>
</div>
<div class="grid-2">
  <div class="field"><div class="field-label">Solicitante</div><div class="field-value">${req.solicitante_nombre || '—'}</div></div>
  <div class="field"><div class="field-label">Proveedor Sugerido</div><div class="field-value">${req.proveedor_nombre || '—'}</div></div>
</div>
${req.observacion1 ? `<div class="grid-1"><div class="field"><div class="field-label">Observaciones</div><div class="field-value">${req.observacion1}${req.observacion2 ? ' · ' + req.observacion2 : ''}</div></div></div>` : ''}
<div class="section-header">Artículos Solicitados</div>
<table>
  <thead><tr>
    <th style="width:28px" class="center">#</th>
    <th style="width:88px">Código</th>
    <th>Descripción del Artículo</th>
    <th style="width:60px" class="center">Cantidad</th>
    <th>Observación</th>
  </tr></thead>
  <tbody>
    ${det.map((d, i) => `<tr>
      <td class="center" style="color:#94a3b8">${i + 1}</td>
      <td style="font-family:monospace;font-size:9.5px">${d.articulo_codigo || '—'}</td>
      <td style="font-weight:500">${d.articulo_descripcion || '—'}</td>
      <td class="center" style="font-weight:700">${d.cantidad ?? 1}</td>
      <td style="color:#64748b">${d.observacion || ''}</td>
    </tr>`).join('')}
    ${det.length === 0 ? '<tr><td colspan="5" class="center" style="color:#94a3b8;padding:12px">Sin artículos</td></tr>' : ''}
  </tbody>
  <tfoot><tr>
    <td colspan="3" style="text-align:right;padding-right:12px">Total de artículos:</td>
    <td class="center">${det.length}</td><td></td>
  </tr></tfoot>
</table>
${req.estado === 'APROBADO' && req.usuario_aprobacion ? `
<div class="section-header">Aprobación</div>
<div class="grid-3">
  <div class="field"><div class="field-label">Aprobado por</div><div class="field-value">${req.usuario_aprobacion}</div></div>
  <div class="field"><div class="field-label">Fecha</div><div class="field-value">${fmt(req.fecha_aprobacion)}</div></div>
  ${req.observacion_aprobacion ? `<div class="field"><div class="field-label">Observación</div><div class="field-value">${req.observacion_aprobacion}</div></div>` : '<div></div>'}
</div>` : ''}
<div class="page-footer">
  <span>${emp.razon_social || ''} — RUC: ${emp.ruc_empresa || ''}</span>
  <span>Impreso: ${new Date().toLocaleDateString('es-PE', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
</div>
</body></html>`;
      const w = window.open('', '_blank', 'width=900,height=750');
      w.document.write(html);
      w.document.close();
      w.focus();
    } catch { setAlert({ type: 'error', message: 'Error al generar vista previa del requerimiento.' }); }
  };

  const handlePrint = (r) => doPrint(r);

  const cellSm = { fontSize: 11 };
  const cellXs = { fontSize: 10 };

  // ── Columnas lista ──
  const tableColumns = [
    { key: 'sucursal_nombre', label: 'Sucursal', width: 90, style: cellSm },
    { key: 'serie_codigo', label: 'Serie', width: 60, style: cellSm },
    { key: 'numero', label: 'Número', width: 72, style: { fontSize: 11, fontWeight: 600 } },
    { key: 'fecha_emision', label: 'F. Emisión', width: 88, style: cellSm, render: (row) => row.fecha_emision?.slice(0, 10) || '-' },
    { key: 'proveedor_nombre', label: 'Proveedor', style: cellSm },
    { key: 'forma_pago_descripcion', label: 'F. Pago', width: 100, style: cellXs },
    { key: '_ref1', label: 'Ref.1', width: 110, style: cellXs,
      render: (row) => row.num_doc_ref1
        ? <span title={row.tipo_doc_ref1_descripcion || ''}>{row.tipo_doc_ref1_descripcion ? row.tipo_doc_ref1_descripcion.slice(0, 4) + '. ' : ''}{row.num_doc_ref1}</span>
        : <span style={{ color: '#475569' }}>—</span>
    },
    { key: 'moneda', label: 'Mon.', width: 48, style: cellXs },
    { key: 'total_soles', label: 'Importe', width: 108, style: cellSm,
      render: (row) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
        {row.moneda === 'USD' ? 'US$ ' : 'S/ '}{fmtMon(row.moneda === 'USD' ? row.total_dolares : row.total_soles)}
      </span>
    },
    { key: 'origen', label: 'Origen', width: 72, style: cellXs,
      render: (row) => row.origen === 'Requerimiento'
        ? <button onClick={() => abrirOrigenModal(row)} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:600, color:'#60a5fa', background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.25)', borderRadius:5, padding:'2px 7px', whiteSpace:'nowrap', cursor:'pointer', transition:'all 0.12s' }}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(96,165,250,0.2)'; e.currentTarget.style.borderColor='rgba(96,165,250,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(96,165,250,0.1)'; e.currentTarget.style.borderColor='rgba(96,165,250,0.25)'; }}
            title="Ver requerimientos de origen">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
            RQ
          </button>
        : <span style={{ fontSize:10, color:'#64748b' }}>Manual</span>
    },
    { key: 'estado', label: 'Estado', width: 80, render: (row) => <span className={`oc-estado ${ESTADO_COLORS[row.estado] || ''}`}>{row.estado}</span> },
    { key: '_pe', label: 'Ingreso PE', width: 72, style: cellXs,
      render: (row) => (row.num_pe > 0 && row.estado !== 'ANULADO')
        ? <button onClick={() => abrirPeModal(row)} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:600, color:'#34d399', background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.25)', borderRadius:5, padding:'2px 7px', whiteSpace:'nowrap', cursor:'pointer', transition:'all 0.12s' }}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(52,211,153,0.2)'; e.currentTarget.style.borderColor='rgba(52,211,153,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(52,211,153,0.1)'; e.currentTarget.style.borderColor='rgba(52,211,153,0.25)'; }}
            title="Ver partes de entrada generadas">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
            PE
          </button>
        : <span style={{ fontSize:10, color:'#374151' }}>—</span>
    },
    { key: '_acciones', label: 'Acciones', width: 295,
      render: (r) => (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
          {/* Ver */}
          <button className="rq-btn-icon rq-btn-view" title="Ver detalle" onClick={() => abrirViewOC(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
          </button>
          {/* Imprimir */}
          <button className="rq-btn-icon rq-btn-print" title="Imprimir OC" onClick={() => handlePrint(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9v-1h8v1H6zm8 2H6v-1h8v1z" clipRule="evenodd"/></svg>
          </button>
          {/* Editar — solo en PENDIENTE o DESAPROBADO y sin documentos derivados */}
          {(r.estado === 'PENDIENTE' || r.estado === 'DESAPROBADO') && (
            <button
              className="rq-btn-icon rq-btn-edit"
              title={r.bloqueado ? 'No editable: tiene partes de entrada derivados' : 'Editar'}
              onClick={() => openEdit(r)}
              disabled={!!r.bloqueado}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
            </button>
          )}
          {/* Duplicar */}
          <button className="rq-btn-icon rq-btn-duplicar" title="Duplicar orden" onClick={() => handleDuplicar(r)}>
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z"/><path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z"/></svg>
          </button>
          {/* Eliminar */}
          <button
            className="rq-btn-icon rq-btn-del"
            title={r.bloqueado ? 'No eliminable: tiene partes de entrada derivados' : 'Eliminar'}
            onClick={() => handleDelete(r)}
            disabled={!!r.bloqueado}
            style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
          </button>
          <div className="rq-btn-sep" />
          {/* Acciones de estado */}
          {r.estado === 'PENDIENTE' && (<>
            <button className="rq-btn-aprobar" onClick={() => handleAprobar(r)}>
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
              Aprobar
            </button>
            <button
              className="rq-btn-anular"
              onClick={() => handleAnular(r)}
              disabled={!!r.bloqueado}
              title={r.bloqueado ? 'No anulable: tiene partes de entrada derivados' : 'Anular'}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>
              Anular
            </button>
          </>)}
          {r.estado === 'APROBADO' && (
            <button
              className="rq-btn-desaprobar"
              onClick={() => handleDesaprobar(r)}
              disabled={!!r.bloqueado}
              title={r.bloqueado ? 'No desaprobable: tiene partes de entrada derivados' : 'Desaprobar'}
              style={r.bloqueado ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10"><path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854z"/></svg>
              Desaprobar
            </button>
          )}
          {r.estado === 'DESAPROBADO' && (
            <button className="rq-btn-volver" onClick={() => handleVolverPendiente(r)}>
              ↩ Pendiente
            </button>
          )}
        </div>
      )},
  ];

  const monedaSimbolo = form?.moneda === 'USD' ? 'US$' : 'S/';

  // ════════════════════════════════════════════════════
  // VISTA LISTA
  // ════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <Layout title="Órdenes de Compra" subtitle="Logística">
        <div className="oc-page-header">
          <h2>Órdenes de Compra</h2>
          <div className="oc-header-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setShowFilters(f => !f)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filtros
              {(filters.sucursal || filters.serie || filters.numero || filters.proveedor || filters.estado || filters.tipoDocRef1 || filters.numDocRef1) && (
                <span style={{ marginLeft: 6, background: '#3b82f6', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>!</span>
              )}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => exportRef.current?.()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Exportar
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => templateRef.current?.()} title="Descargar plantilla de importación">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Plantilla
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => importRef.current?.()} title="Importar desde Excel">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}><path d="M3 15v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Importar
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkDelete(true)}
              disabled={filteredRecords.length === 0}
              title={filteredRecords.length === 0 ? 'No hay registros para eliminar' : 'Eliminar todas las OCs visibles'}
              style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
              Eliminar todos
            </button>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Nueva OC</button>
          </div>
        </div>

        {showFilters && (
          <div className="oc-filter-panel">
            <div className="oc-filter-grid">
              <div className="oc-filter-field">
                <label className="oc-filter-label">Sucursal</label>
                <AutoComplete
                  options={filterSucursales}
                  value={filters.sucursal || null}
                  idField="id"
                  displayField="nombre"
                  placeholder="Todas las sucursales"
                  onChange={(v) => setFilters(p => ({ ...p, sucursal: v || '' }))}
                />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Serie</label>
                <AutoComplete
                  options={filterSeries}
                  value={filters.serie || null}
                  idField="id"
                  displayField="serie"
                  placeholder="Todas las series"
                  onChange={(v) => setFilters(p => ({ ...p, serie: v || '' }))}
                />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Número</label>
                <input className="oc-filter-input" placeholder="Buscar número..." value={filters.numero} onChange={e => setFilters(p => ({ ...p, numero: e.target.value }))} />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Fecha Inicio</label>
                <input className="oc-filter-date" type="date" value={filters.fechaInicio} onChange={e => setFilters(p => ({ ...p, fechaInicio: e.target.value }))} />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Fecha Fin</label>
                <input className="oc-filter-date" type="date" value={filters.fechaFin} onChange={e => setFilters(p => ({ ...p, fechaFin: e.target.value }))} />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Proveedor</label>
                <AutoComplete
                  options={filterProveedores}
                  value={filters.proveedor || null}
                  idField="id"
                  displayField={(p) => p.ruc ? `${p.ruc} — ${p.nombre}` : p.nombre}
                  placeholder="Todos los proveedores"
                  onChange={(v) => setFilters(p => ({ ...p, proveedor: v || '' }))}
                />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Estado</label>
                <select className="oc-filter-select" value={filters.estado} onChange={e => setFilters(p => ({ ...p, estado: e.target.value }))}>
                  <option value="">Todos</option>
                  <option>PENDIENTE</option><option>APROBADO</option>
                  <option>DESAPROBADO</option><option>ANULADO</option>
                </select>
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">Tipo Doc. Ref. 1</label>
                <AutoComplete
                  options={filterTiposDocRef1}
                  value={filters.tipoDocRef1 || null}
                  idField="id"
                  displayField="nombre"
                  placeholder="Todos los tipos"
                  onChange={(v) => setFilters(p => ({ ...p, tipoDocRef1: v || '' }))}
                />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">N° Doc. Ref. 1</label>
                <input className="oc-filter-input" placeholder="Buscar número ref..." value={filters.numDocRef1} onChange={e => setFilters(p => ({ ...p, numDocRef1: e.target.value }))} />
              </div>
              <div className="oc-filter-field">
                <label className="oc-filter-label">&nbsp;</label>
                <button className="btn btn-secondary btn-sm" style={{ height: 30, width: '100%', fontSize: 12 }} onClick={() => setFilters({ sucursal: '', serie: '', numero: '', fechaInicio: weekAgo(), fechaFin: today(), proveedor: '', estado: '', tipoDocRef1: '', numDocRef1: '' })}>Limpiar filtros</button>
              </div>
            </div>
          </div>
        )}

        <div className="oc-list-wrap">
          <DataTable
            columns={tableColumns}
            data={filteredRecords}
            idField="id_orden"
            loading={loading}
            emptyMessage="No hay órdenes de compra registradas"
            exportFileName="ordenes_compra"
            hideSearch
            hideToolbarRight
            importColumns={OC_IMPORT_COLUMNS}
            exportRef={exportRef}
            importRef={importRef}
            templateRef={templateRef}
          />
        </div>

        {/* ── Modal Ver OC ── */}
        {viewModal && (
          <div style={{ position:'fixed', inset:0, zIndex:9400, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}
            onClick={() => setViewModal(null)}>
            <div style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, width:'min(960px,96vw)', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Cabecera */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:'rgba(20,184,166,0.15)', border:'1px solid rgba(20,184,166,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" style={{ color:'#2dd4bf' }}><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:700, color:'#e2e8f0' }}>
                      OC {viewModal.serie_codigo ? `${viewModal.serie_codigo}-` : ''}{viewModal.numero}
                    </div>
                    <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>Vista rápida de la orden de compra</div>
                  </div>
                </div>
                <button onClick={() => setViewModal(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:22, cursor:'pointer', lineHeight:1, padding:'0 4px' }}>×</button>
              </div>

              <div style={{ overflowY:'auto', flex:1, padding:'16px 20px' }}>
                {loadingView && <div style={{ textAlign:'center', color:'#64748b', padding:40 }}>Cargando...</div>}
                {viewData && (() => {
                  const oc    = viewData;
                  const det   = oc.detalle || [];
                  const fmt   = d => d ? d.split('T')[0] : '—';
                  const fmtN  = (v, dec=2) => (parseFloat(v)||0).toLocaleString('es-PE',{minimumFractionDigits:dec,maximumFractionDigits:dec});
                  const fmtV  = v => parseFloat(v) > 0;
                  const esPEN = oc.moneda === 'PEN';
                  const sym   = esPEN ? 'S/' : 'US$';
                  const ECOL  = { PENDIENTE:'#f59e0b', APROBADO:'#22c55e', DESAPROBADO:'#fb923c', ANULADO:'#64748b' };
                  const EBG   = { PENDIENTE:'rgba(245,158,11,0.1)', APROBADO:'rgba(34,197,94,0.1)', DESAPROBADO:'rgba(251,146,60,0.1)', ANULADO:'rgba(100,116,139,0.1)' };
                  const baseImp = esPEN ? oc.base_imponible_soles : oc.base_imponible_dolares;
                  const igvT    = esPEN ? oc.igv_soles            : oc.igv_dolares;
                  const noGrav  = esPEN ? oc.no_gravado_soles     : oc.no_gravado_dolares;
                  const descT   = esPEN ? oc.descuento_soles      : oc.descuento_dolares;
                  const total   = esPEN ? oc.total_soles          : oc.total_dolares;

                  const SecTitle = ({ children, first }) => (
                    <div style={{ display:'flex', alignItems:'center', gap:8, margin: first ? '0 0 8px' : '16px 0 8px' }}>
                      <span style={{ fontSize:9.5, fontWeight:700, color:'#60a5fa', textTransform:'uppercase', letterSpacing:'1px', whiteSpace:'nowrap' }}>{children}</span>
                      <div style={{ flex:1, height:1, background:'rgba(96,165,250,0.2)' }} />
                    </div>
                  );
                  // w: ancho fijo en px, 'full' = fila completa
                  const Cell = ({ label, value, w }) => (
                    <div style={{ flex: w === 'full' ? '1 1 100%' : `0 0 ${w}px`, minWidth:0, paddingBottom:8 }}>
                      <div style={{ fontSize:9, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'.6px', marginBottom:2 }}>{label}</div>
                      <div style={{ fontSize:12.5, color:'#e2e8f0', fontWeight:400, lineHeight:1.35, wordBreak:'break-word' }}>{value ?? '—'}</div>
                    </div>
                  );
                  const Row = ({ children }) => (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'0 16px' }}>{children}</div>
                  );

                  return (<>
                    {/* ── Documento ── */}
                    <SecTitle first>Datos del Documento</SecTitle>
                    <Row>
                      <Cell label="Serie - Número" w={155} value={`${oc.serie_codigo ? oc.serie_codigo.trim()+'-' : ''}${oc.numero}`} />
                      <Cell label="Tipo OC"         w={110} value={oc.tipo_oc} />
                      <Cell label="Estado"          w={120} value={<span style={{ background:EBG[oc.estado], color:ECOL[oc.estado], borderRadius:12, padding:'2px 9px', fontSize:11, fontWeight:700 }}>{oc.estado}</span>} />
                      <Cell label="F. Emisión"      w={105} value={fmt(oc.fecha_emision)} />
                      <Cell label="F. Entrega"      w={105} value={fmt(oc.fecha_entrega)} />
                      <Cell label="Sucursal"        w={200} value={oc.sucursal_nombre} />
                      <Cell label="Solicitante"     w={200} value={oc.solicitante_nombre} />
                      <Cell label="Serie"           w={160} value={oc.serie_descripcion || oc.serie_codigo} />
                    </Row>

                    {/* ── Proveedor ── */}
                    <SecTitle>Proveedor</SecTitle>
                    <Row>
                      <Cell label="Razón Social"   w={280} value={oc.proveedor_nombre} />
                      <Cell label="RUC"            w={120} value={oc.proveedor_ruc} />
                      <Cell label="Forma de Pago"  w={160} value={oc.forma_pago_descripcion} />
                      {oc.persona_atencion_nombre && <Cell label="Persona de Atención" w={200} value={oc.persona_atencion_nombre} />}
                    </Row>
                    {oc.proveedor_direccion && (
                      <Row>
                        <Cell label="Dirección" w="full" value={oc.proveedor_direccion} />
                      </Row>
                    )}

                    {/* ── Condiciones ── */}
                    <SecTitle>Condiciones Económicas</SecTitle>
                    <Row>
                      <Cell label="Moneda"          w={130} value={esPEN ? 'Soles (PEN)' : 'Dólares (USD)'} />
                      <Cell label="Tipo de Cambio"  w={120} value={`S/ ${fmtN(oc.tipo_cambio, 4)}`} />
                      <Cell label="Descuento Gral." w={115} value={`${fmtN(oc.descuento_porcentaje, 2)} %`} />
                      <Cell label="Total a Pagar"   w={170} value={<span style={{ fontWeight:700, color:'#e2e8f0', fontSize:13 }}>{sym} {fmtN(total)}</span>} />
                    </Row>

                    {/* ── Almacenes ── */}
                    {(oc.almacen_entrega_nombre || oc.almacen_factura_nombre) && (<>
                      <SecTitle>Almacenes</SecTitle>
                      <Row>
                        {oc.almacen_entrega_nombre && <Cell label="Almacén de Entrega" w={280} value={`${oc.almacen_entrega_nombre}${oc.almacen_entrega_direccion ? ' — '+oc.almacen_entrega_direccion : ''}`} />}
                        {oc.almacen_factura_nombre && <Cell label="Almacén de Factura" w={280} value={`${oc.almacen_factura_nombre}${oc.almacen_factura_direccion ? ' — '+oc.almacen_factura_direccion : ''}`} />}
                      </Row>
                    </>)}

                    {/* ── Documentos de referencia ── */}
                    {(oc.num_doc_ref1 || oc.num_doc_ref2) && (<>
                      <SecTitle>Referencias</SecTitle>
                      <Row>
                        {oc.num_doc_ref1 && <Cell label={`Doc. Ref. 1${oc.tipo_doc_ref1_descripcion ? ' ('+oc.tipo_doc_ref1_descripcion+')' : ''}`} w={220} value={oc.num_doc_ref1} />}
                        {oc.num_doc_ref2 && <Cell label={`Doc. Ref. 2${oc.tipo_doc_ref2_descripcion ? ' ('+oc.tipo_doc_ref2_descripcion+')' : ''}`} w={220} value={oc.num_doc_ref2} />}
                      </Row>
                    </>)}

                    {/* ── Observaciones ── */}
                    {oc.observaciones && (<>
                      <SecTitle>Observaciones</SecTitle>
                      {(() => {
                        const renderObs = (text) => text.split('\n').map((line, li) => (
                          <React.Fragment key={li}>
                            {li > 0 && <br />}
                            {line.split(/(\*[^*\n]+\*|#[^#\n]+#)/).map((part, pi) => {
                              if (/^\*[^*]+\*$/.test(part)) return <strong key={pi}>{part.slice(1,-1)}</strong>;
                              if (/^#[^#]+#$/.test(part)) return <span key={pi} style={{ background:'#2563eb', color:'#fff', borderRadius:3, padding:'0 4px', fontWeight:600 }}>{part.slice(1,-1)}</span>;
                              return part;
                            })}
                          </React.Fragment>
                        ));
                        return (
                          <Row>
                            <Cell label="Obs." w="full" value={<span style={{ whiteSpace:'pre-wrap', lineHeight:1.6 }}>{renderObs(oc.observaciones)}</span>} />
                          </Row>
                        );
                      })()}
                    </>)}

                    {/* ── Detalle de artículos ── */}
                    <SecTitle>Detalle de Artículos</SecTitle>
                    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, overflow:'hidden', marginBottom:10 }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                        <thead>
                          <tr style={{ background:'rgba(255,255,255,0.05)' }}>
                            {[
                              { h:'#',           align:'center' },
                              { h:'Código',      align:'left'   },
                              { h:'Artículo',    align:'left'   },
                              { h:'Cant.',       align:'right'  },
                              { h:'Precio Unit.', align:'right' },
                              { h:'%IGV',        align:'center' },
                              { h:'IGV Unit.',   align:'right'  },
                              { h:'Desc.%',      align:'center' },
                              { h:'Total Línea', align:'right'  },
                              { h:'RQ Origen',   align:'center' },
                              { h:'Rev.',        align:'center' },
                            ].map(({ h, align }) => (
                              <th key={h} style={{ padding:'7px 9px', color:'#64748b', fontSize:9.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', textAlign:align, borderBottom:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {det.map((d, i) => {
                            const precio = esPEN ? d.precio_soles        : d.precio_dolares;
                            const igvU   = esPEN ? d.igv_soles           : d.igv_dolares;
                            const totalL = esPEN ? d.total_con_descuento_soles : d.total_con_descuento_dolares;
                            const rqLabel = d.rq_numero ? `${d.rq_serie ? d.rq_serie.trim()+'-' : ''}${d.rq_numero}` : null;
                            return (
                              <tr key={d.id_detalle || i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                                <td style={{ padding:'6px 9px', color:'#475569', textAlign:'center', fontSize:10.5 }}>{i+1}</td>
                                <td style={{ padding:'6px 9px', color:'#94a3b8', fontFamily:'monospace', fontSize:10, whiteSpace:'nowrap' }}>{d.articulo_codigo || '—'}</td>
                                <td style={{ padding:'6px 9px', color:'#e2e8f0', fontWeight:500 }}>{d.articulo_descripcion || '—'}{d.observacion ? <span style={{ display:'block', fontSize:10, color:'#64748b', marginTop:1 }}>{d.observacion}</span> : null}</td>
                                <td style={{ padding:'6px 9px', textAlign:'right', color:'#e2e8f0', fontWeight:600, whiteSpace:'nowrap' }}>{(parseFloat(d.cantidad)||0).toLocaleString('es-PE',{maximumFractionDigits:4})}</td>
                                <td style={{ padding:'6px 9px', textAlign:'right', color:'#cbd5e1', whiteSpace:'nowrap' }}>{sym} {fmtN(precio,4)}</td>
                                <td style={{ padding:'6px 9px', textAlign:'center', color:'#94a3b8' }}>{d.porcentaje_igv ?? 18}%</td>
                                <td style={{ padding:'6px 9px', textAlign:'right', color:'#94a3b8', whiteSpace:'nowrap' }}>{sym} {fmtN(igvU,4)}</td>
                                <td style={{ padding:'6px 9px', textAlign:'center', color: parseFloat(d.porcentaje_descuento)>0 ? '#fb923c' : '#475569' }}>{fmtN(d.porcentaje_descuento,2)}%</td>
                                <td style={{ padding:'6px 9px', textAlign:'right', color:'#e2e8f0', fontWeight:700, whiteSpace:'nowrap' }}>{sym} {fmtN(totalL,2)}</td>
                                <td style={{ padding:'6px 9px', textAlign:'center' }}>
                                  {rqLabel
                                    ? <span style={{ fontSize:10.5, fontWeight:600, color:'#60a5fa', background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:5, padding:'1px 6px', whiteSpace:'nowrap' }}>{rqLabel}</span>
                                    : <span style={{ color:'#374151', fontSize:10 }}>—</span>}
                                </td>
                                <td style={{ padding:'6px 9px', textAlign:'center' }}>
                                  {d.revisado
                                    ? <span style={{ color:'#22c55e', fontSize:13 }}>✓</span>
                                    : <span style={{ color:'#374151', fontSize:11 }}>·</span>}
                                </td>
                              </tr>
                            );
                          })}
                          {det.length === 0 && (
                            <tr><td colSpan={11} style={{ textAlign:'center', color:'#475569', padding:20, fontStyle:'italic' }}>Sin artículos registrados</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* ── Totales ── */}
                    <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
                      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, overflow:'hidden', minWidth:280 }}>
                        {fmtV(baseImp) && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:12 }}>
                            <span style={{ color:'#64748b' }}>Base Imponible</span>
                            <span style={{ color:'#cbd5e1', fontWeight:600 }}>{sym} {fmtN(baseImp,2)}</span>
                          </div>
                        )}
                        {fmtV(igvT) && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:12 }}>
                            <span style={{ color:'#64748b' }}>IGV</span>
                            <span style={{ color:'#cbd5e1', fontWeight:600 }}>{sym} {fmtN(igvT,2)}</span>
                          </div>
                        )}
                        {fmtV(noGrav) && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:12 }}>
                            <span style={{ color:'#64748b' }}>No Gravado</span>
                            <span style={{ color:'#cbd5e1', fontWeight:600 }}>{sym} {fmtN(noGrav,2)}</span>
                          </div>
                        )}
                        {fmtV(descT) && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:12 }}>
                            <span style={{ color:'#64748b' }}>Descuentos</span>
                            <span style={{ color:'#fb923c', fontWeight:600 }}>− {sym} {fmtN(descT,2)}</span>
                          </div>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between', padding:'9px 14px', background:'rgba(37,99,235,0.12)', fontSize:13 }}>
                          <span style={{ color:'#93c5fd', fontWeight:700 }}>TOTAL A PAGAR</span>
                          <span style={{ color:'#e2e8f0', fontWeight:800 }}>{sym} {fmtN(total)}</span>
                        </div>
                        {!esPEN && (
                          <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 14px', fontSize:11 }}>
                            <span style={{ color:'#475569' }}>Equivalente soles</span>
                            <span style={{ color:'#64748b' }}>S/ {fmtN(oc.total_soles)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Aprobación ── */}
                    {oc.usuario_aprobacion && (<>
                      <SecTitle>Aprobación</SecTitle>
                      <Row>
                        <Cell label="Aprobado / Procesado por" w={180} value={oc.usuario_aprobacion} />
                        <Cell label="Fecha de Aprobación"      w={140} value={fmt(oc.fecha_aprobacion)} />
                        {oc.observacion_aprobacion && <Cell label="Obs. Aprobación" w={300} value={oc.observacion_aprobacion} />}
                      </Row>
                    </>)}

                    {/* ── Auditoría ── */}
                    <SecTitle>Auditoría</SecTitle>
                    <Row>
                      <Cell label="Creado por"        w={160} value={oc.usuario_creacion} />
                      <Cell label="Última modif. por" w={160} value={oc.usuario_modificacion} />
                      <Cell label="Fecha de Creación" w={140} value={fmt(oc.fecha_creacion)} />
                    </Row>
                  </>);
                })()}
              </div>

              <div style={{ padding:'11px 20px', borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setViewModal(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal Origen RQ ── */}
        {origenModal && (
          <div style={{ position:'fixed', inset:0, zIndex:9500, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}
            onClick={() => setOrigenModal(null)}>
            <div style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, width:'min(540px,94vw)', maxHeight:'80vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>
                    Requerimientos de Origen
                  </div>
                  <div style={{ fontSize:11.5, color:'#64748b', marginTop:2 }}>
                    OC {origenModal.serie_codigo ? `${origenModal.serie_codigo}-` : ''}{origenModal.numero}
                  </div>
                </div>
                <button onClick={() => setOrigenModal(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:22, cursor:'pointer', lineHeight:1, padding:'0 4px' }}>×</button>
              </div>
              <div style={{ overflowY:'auto', padding:'16px 20px', flex:1 }}>
                {loadingOrigen && <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>Cargando...</div>}
                {!loadingOrigen && origenRQs.length === 0 && (
                  <div style={{ textAlign:'center', color:'#475569', padding:24, fontStyle:'italic' }}>No se encontraron requerimientos vinculados.</div>
                )}
                {origenRQs.map((rq, i) => (
                  <div key={rq.id_rq} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', marginBottom:8, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:11, color:'#64748b', fontWeight:600, minWidth:18 }}>{i + 1}.</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#60a5fa' }}>
                          RQ {rq.serie ? `${rq.serie}-` : ''}{rq.numero || '—'}
                        </div>
                        <div style={{ fontSize:10.5, color:'#64748b', marginTop:2 }}>Requerimiento #{rq.id_rq}</div>
                      </div>
                    </div>
                    <button onClick={() => previewRQ(rq.id_rq)}
                      style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 12px', fontSize:11, fontWeight:600, borderRadius:6, border:'1px solid rgba(96,165,250,0.35)', background:'rgba(96,165,250,0.08)', color:'#60a5fa', cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.12s' }}
                      onMouseEnter={e => { e.currentTarget.style.background='rgba(96,165,250,0.18)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background='rgba(96,165,250,0.08)'; }}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                      Ver RQ
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setOrigenModal(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal Ingreso PE ── */}
        {peModal && (
          <div style={{ position:'fixed', inset:0, zIndex:9500, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
            <div style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, width:'min(760px,96vw)', maxHeight:'84vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px 12px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#e2e8f0' }}>Partes de Entrada generadas</div>
                  <div style={{ fontSize:11.5, color:'#64748b', marginTop:2 }}>
                    OC {peModal.serie_codigo ? `${peModal.serie_codigo}-` : ''}{peModal.numero} — {peModal.proveedor_nombre}
                  </div>
                </div>
                <button onClick={() => setPeModal(null)} style={{ background:'none', border:'none', color:'#94a3b8', fontSize:22, cursor:'pointer', lineHeight:1, padding:'0 4px' }}>×</button>
              </div>
              <div style={{ overflowY:'auto', padding:'16px 20px', flex:1 }}>
                {loadingPe && <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>Cargando...</div>}
                {!loadingPe && peAtencionData.length === 0 && (
                  <div style={{ textAlign:'center', color:'#475569', padding:24, fontStyle:'italic' }}>No hay partes de entrada registradas para esta OC.</div>
                )}
                {!loadingPe && peAtencionData.length > 0 && (
                  <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, overflow:'hidden' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                      <thead>
                        <tr style={{ background:'rgba(255,255,255,0.05)' }}>
                          {['PE Número','F. Operación','Almacén','Artículo','Cantidad','Creado por'].map(h => (
                            <th key={h} style={{ padding:'7px 10px', color:'#64748b', fontSize:9.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {peAtencionData.map((d, i) => (
                          <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding:'6px 10px', fontWeight:700, color:'#34d399', whiteSpace:'nowrap' }}>PE-{d.numero}</td>
                            <td style={{ padding:'6px 10px', color:'#cbd5e1', whiteSpace:'nowrap' }}>{d.fecha_operacion?.slice(0,10) || '—'}</td>
                            <td style={{ padding:'6px 10px', color:'#94a3b8' }}>{d.almacen_nombre || '—'}</td>
                            <td style={{ padding:'6px 10px', color:'#e2e8f0', fontWeight:500 }}>
                              {d.articulo_codigo ? <span style={{ fontSize:10, color:'#64748b', fontFamily:'monospace' }}>[{d.articulo_codigo}]</span> : null}
                              {' '}{d.articulo_descripcion || '—'}
                            </td>
                            <td style={{ padding:'6px 10px', textAlign:'right', color:'#e2e8f0', fontWeight:600, whiteSpace:'nowrap' }}>
                              {(parseFloat(d.cantidad)||0).toLocaleString('es-PE',{maximumFractionDigits:4})}
                            </td>
                            <td style={{ padding:'6px 10px', color:'#64748b', fontSize:11 }}>{d.usuario_creacion || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setPeModal(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
        {confirm && <Confirm title={confirm.title} message={confirm.message} confirmLabel={confirm.confirmLabel} confirmStyle={confirm.confirmStyle} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
        {toast && <Toast message={toast} onClose={() => setToast(null)} />}

        {showBulkDelete && (
          <ConfirmDestructive
            title="Eliminar órdenes de compra"
            message="Esta acción eliminará todas las OCs que se muestran actualmente en la lista (respetando los filtros aplicados)."
            count={filteredRecords.length}
            loading={bulkDeleteLoading}
            onCancel={() => !bulkDeleteLoading && setShowBulkDelete(false)}
            onConfirm={async () => {
              setBulkDeleteLoading(true);
              try {
                const ids = filteredRecords.map(r => r.id_orden);
                const res = await ordenCompraService.deleteBulk(ids);
                const { ok = 0, fail = 0, errores = [] } = res.data || {};
                setShowBulkDelete(false);
                fetchAll();
                if (fail === 0) {
                  setToast(`${ok} orden${ok !== 1 ? 'es' : ''} de compra eliminada${ok !== 1 ? 's' : ''}`);
                } else {
                  setAlert({
                    type: 'warning',
                    message: `Eliminadas: ${ok}. Omitidas por tener partes de entrada activas: ${fail}.` +
                      (errores.length ? '\n\nPrimeros motivos:\n' + errores.slice(0, 5).join('\n') : ''),
                  });
                }
              } catch (err) {
                setAlert({ type: 'error', message: getErrorDetail(err, 'Error al eliminar registros.') });
              } finally {
                setBulkDeleteLoading(false);
              }
            }}
          />
        )}
      </Layout>
    );
  }

  // ════════════════════════════════════════════════════
  // VISTA FORMULARIO (página completa)
  // ════════════════════════════════════════════════════
  if (!form) return null;

  return (
    <Layout title="Órdenes de Compra" subtitle="Logística">
      <div className="oc-form-page">
        {/* Cabecera fija */}
        <div className="oc-form-header">
          <div className="oc-form-page-title">
            <button className="oc-back-btn" onClick={() => setView('list')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>
                {editing ? `Editar OC — ${editing.serie_codigo ? editing.serie_codigo.trim() + '-' : ''}${editing.numero}` : 'Nueva Orden de Compra'}
              </h2>
              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Logística</p>
            </div>
          </div>
          <div className="oc-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setView('list')}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Guardando...' : (editing ? 'Guardar Cambios' : 'Crear Orden de Compra')}
            </button>
          </div>
        </div>

        {/* Cuerpo scrollable */}
        <div className="oc-form-body">
        <form onSubmit={handleSubmit}>
        {/* ── SECCIÓN 1: Identificación ── */}
        <div className="oc-form-card">
          <div className="oc-section-title">Identificación</div>
          <div className="oc-form-grid oc-form-grid-8">
            <div>
              <label className="oc-field-label">Sucursal</label>
              <input className="oc-field-input" value={form.sucursal_nombre || 'Sin asignar'} readOnly />
            </div>
            <div>
              <label className="oc-field-label">Tipo Doc.</label>
              <input className="oc-field-input" value="OC" readOnly />
            </div>
            <div className={!form.id_serie ? 'oc-req-empty' : ''}>
              <AutoComplete
                label="Serie *"
                options={series}
                value={form.id_serie}
                idField="id_serie"
                displayField={(s) => `${s.serie} — ${s.descripcion}`}
                placeholder="Serie OC..."
                onChange={(v) => {
                  setForm(prev => ({ ...prev, id_serie: v || null }));
                  if (v) generarNumero(v);
                  else setForm(prev => ({ ...prev, numero: '' }));
                }}
              />
            </div>
            <div>
              <label className="oc-field-label">Número *</label>
              <input className="oc-field-input" value={form.numero} onChange={e => setForm(prev => ({ ...prev, numero: e.target.value }))} placeholder="Auto-generado"
                style={!form.numero ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}} />
            </div>
            <div>
              <label className="oc-field-label">Tipo OC</label>
              <select className="oc-field-select" value={form.tipo_oc} onChange={e => setForm(prev => ({ ...prev, tipo_oc: e.target.value }))}>
                <option value="NACIONAL">Nacional</option>
                <option value="IMPORTACION">Importación</option>
                <option value="SERVICIOS">Servicios</option>
              </select>
            </div>
            <div>
              <label className="oc-field-label">F. Emisión *</label>
              <input className="oc-field-input" type="date" value={form.fecha_emision} onChange={e => setForm(prev => ({ ...prev, fecha_emision: e.target.value }))}
                style={!form.fecha_emision ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}} />
            </div>
            <div>
              <label className="oc-field-label">F. Entrega</label>
              <input className="oc-field-input" type="date" value={form.fecha_entrega} onChange={e => setForm(prev => ({ ...prev, fecha_entrega: e.target.value }))} />
            </div>
            <div>
              <label className="oc-field-label">Moneda *</label>
              <select className="oc-field-select" value={form.moneda} onChange={e => setForm(prev => ({ ...prev, moneda: e.target.value }))}>
                <option value="PEN">Soles (PEN)</option>
                <option value="USD">Dólares (USD)</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 170 }}>
              <label className="oc-field-label">Tipo de Cambio (venta) *</label>
              <input className="oc-field-input" type="number" step="0.0001" min="0.0001"
                value={form.tipo_cambio}
                onChange={e => { setForm(prev => ({ ...prev, tipo_cambio: e.target.value })); setTcSource('manual'); }}
                placeholder="0.0000"
                style={!form.tipo_cambio || parseFloat(form.tipo_cambio) <= 0 ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}}
              />
              <div className={`oc-tc-source ${tcSource}`}>
                {tcSource === 'db' && '✓ Obtenido de la base de datos'}
                {tcSource === 'sunat' && '✓ Obtenido de SUNAT y guardado'}
                {tcSource === 'manual' && form.moneda === 'USD' && '⚠ Ingrese el tipo de cambio'}
              </div>
            </div>
          </div>
        </div>

        {/* ── SECCIÓN 2: Proveedor ── */}
        <div className="oc-form-card">
          <div className="oc-section-title">Proveedor</div>
          <div className="oc-form-grid oc-form-grid-2">
            <div className={!form.id_proveedor ? 'oc-req-empty' : ''}>
              <AutoComplete
                label="Proveedor (RUC / Razón Social) *"
                options={entidades}
                value={form.id_proveedor}
                idField="id_entidad"
                displayField={(e) => `${e.ruc || ''} — ${e.razon_social}`}
                placeholder="Buscar proveedor..."
                onChange={(v) => {
                  const ent = entidades.find(x => x.id_entidad === v);
                  setForm(prev => ({
                    ...prev, id_proveedor: v || null,
                    proveedor_nombre: ent?.razon_social || '',
                    proveedor_ruc: ent?.ruc || '',
                    proveedor_direccion: ent?.direccion || '',
                  }));
                }}
                onAdd={() => { setFormProv(emptyProv); setModalProv(true); }}
                addLabel="Proveedor"
              />
            </div>
            <div>
              <label className="oc-field-label">Dirección del Proveedor</label>
              <input className="oc-field-input" value={form.proveedor_direccion} readOnly placeholder="Se carga al seleccionar proveedor" />
            </div>
          </div>
        </div>

        {/* ── SECCIÓN 3: Condiciones ── */}
        <div className="oc-form-card">
          <div className="oc-section-title">Condiciones de Compra</div>
          <div className="oc-form-grid oc-form-grid-6">
            <div className={!form.id_forma_pago ? 'oc-req-empty' : ''}>
              <AutoComplete
                label="Forma de Pago *"
                options={formasPago}
                value={form.id_forma_pago}
                idField="id_forma_pago"
                displayField="descripcion"
                placeholder="Buscar forma de pago..."
                onChange={(v) => setForm(prev => ({ ...prev, id_forma_pago: v || null }))}
              />
            </div>
            <AutoComplete
              label="Solicitante"
              options={trabajadores}
              value={form.id_solicitante}
              idField="id_trabajador"
              displayField="nombres"
              placeholder="Buscar trabajador..."
              onChange={(v) => setForm(prev => ({ ...prev, id_solicitante: v || null }))}
            />
            <div>
              <label className="oc-field-label">Descuento Global (%)</label>
              <input className="oc-field-input" type="number" step="0.01" min="0" max="100"
                value={form.descuento_porcentaje}
                onChange={e => setForm(prev => ({ ...prev, descuento_porcentaje: e.target.value }))}
                placeholder="0.00" />
            </div>
            <AutoComplete
              label="Lugar de Entrega (Almacén)"
              options={almacenes}
              value={form.id_almacen_entrega}
              idField="id_almacen"
              displayField="nombre"
              placeholder="Buscar almacén..."
              onChange={(v) => setForm(prev => ({ ...prev, id_almacen_entrega: v || null }))}
            />
            <div className={!form.id_centro_costo && detalle.some(l => !l.id_centro_costo) ? 'oc-req-empty' : ''}>
              {detalle.length > 1 && !ccUnicoId && ccAgrupadoLabel ? (
                <>
                  <label className="oc-field-label">Centros de Costo (varios) *</label>
                  <div className="oc-field-input" style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(255,255,255,0.03)', cursor: 'not-allowed', padding: '6px 10px', minHeight: 34, display: 'flex', alignItems: 'center' }} title={ccAgrupadoLabel}>
                    {ccAgrupadoLabel.length > 60 ? ccAgrupadoLabel.slice(0, 57) + '...' : ccAgrupadoLabel}
                  </div>
                </>
              ) : (
                <AutoComplete
                  label="Centro de Costo *"
                  options={centrosCosto}
                  value={form.id_centro_costo}
                  idField="id_centro_costo"
                  displayField="display_cc"
                  placeholder="Centro de costo..."
                  onChange={(v) => {
                    setForm(prev => ({ ...prev, id_centro_costo: v || null }));
                    aplicarCCCabecera(v || null);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── SECCIÓN 4: Referencias y atención ── */}
        <div className="oc-form-card">
          <div className="oc-section-title">Referencias y Atención</div>
          <div className="oc-form-grid oc-form-grid-6">
            <div className={!form.id_tipo_doc_ref1 ? 'oc-req-empty' : ''}>
              <AutoComplete
                label="Tipo Doc. Ref. 1 *"
                options={tiposDoc}
                value={form.id_tipo_doc_ref1}
                idField="id_tipo_documento"
                displayField="descripcion"
                placeholder="Tipo documento..."
                onChange={(v) => setForm(prev => ({ ...prev, id_tipo_doc_ref1: v || null }))}
              />
            </div>
            <div>
              <label className="oc-field-label">N° Doc. Ref. 1 *</label>
              <input className="oc-field-input" value={form.num_doc_ref1} onChange={e => setForm(prev => ({ ...prev, num_doc_ref1: e.target.value }))} placeholder="Número..."
                style={!form.num_doc_ref1 ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}} />
            </div>
            <AutoComplete
              label="Tipo Doc. Ref. 2"
              options={tiposDoc}
              value={form.id_tipo_doc_ref2}
              idField="id_tipo_documento"
              displayField="descripcion"
              placeholder="Tipo documento..."
              onChange={(v) => setForm(prev => ({ ...prev, id_tipo_doc_ref2: v || null }))}
            />
            <div>
              <label className="oc-field-label">N° Doc. Ref. 2</label>
              <input className="oc-field-input" value={form.num_doc_ref2} onChange={e => setForm(prev => ({ ...prev, num_doc_ref2: e.target.value }))} placeholder="Número..." />
            </div>
            <AutoComplete
              label="Persona de Atención"
              options={trabajadores}
              value={form.id_persona_atencion}
              idField="id_trabajador"
              displayField="nombres"
              placeholder="Buscar trabajador..."
              onChange={(v) => setForm(prev => ({ ...prev, id_persona_atencion: v || null }))}
            />
            <AutoComplete
              label="Dir. Factura (Almacén)"
              options={almacenes}
              value={form.id_almacen_factura}
              idField="id_almacen"
              displayField={(a) => `${a.nombre}${a.direccion ? ' — ' + a.direccion : ''}`}
              placeholder="Seleccionar almacén..."
              onChange={(v) => setForm(prev => ({ ...prev, id_almacen_factura: v || null }))}
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <label className="oc-field-label" style={{ marginBottom: 0, flexShrink: 0 }}>
                Observaciones —{' '}
                <span style={{ fontWeight: 700, color: '#e2e8f0', textTransform: 'none', letterSpacing: 0 }}>*negrita*</span>
                {' · '}
                <span style={{ background: '#2563eb', color: '#fff', borderRadius: 3, padding: '1px 4px', fontWeight: 700, textTransform: 'none', letterSpacing: 0 }}>#resaltar#</span>
              </label>
              <button type="button" className="oc-obs-btn-lg bold" title="Negrita: *texto*" onClick={() => applyFmtObs('bold')}><b>B</b></button>
              <button type="button" className="oc-obs-btn-lg hl"   title="Resaltar: #texto#"  onClick={() => applyFmtObs('highlight')}>A</button>
            </div>
            <textarea id="oc-obs-main" className="oc-field-textarea" value={form.observaciones}
              onChange={e => setForm(prev => ({ ...prev, observaciones: e.target.value }))}
              placeholder="Observaciones adicionales..." rows={2} />
          </div>
        </div>

        {/* ── SECCIÓN 5: Detalle de Artículos ── */}
        <div className="oc-form-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
            <div className="oc-section-title" style={{ marginBottom: 0 }}>Detalle de Artículos</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Toggle precio con/sin IGV */}
              <div style={{
                display: 'inline-flex', padding: 2, borderRadius: 8,
                background: 'rgba(15,27,45,0.6)', border: '1px solid rgba(255,255,255,0.08)',
              }} title="Modo de captura del precio unitario">
                <button type="button"
                  onClick={() => setPrecioConIgv(false)}
                  style={{
                    padding: '5px 12px', fontSize: 11.5, fontWeight: 600,
                    background: !precioConIgv ? 'rgba(96,165,250,0.18)' : 'transparent',
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    color: !precioConIgv ? '#93c5fd' : '#64748b',
                    transition: 'all 0.15s ease',
                  }}>Precio s/IGV</button>
                <button type="button"
                  onClick={() => setPrecioConIgv(true)}
                  style={{
                    padding: '5px 12px', fontSize: 11.5, fontWeight: 600,
                    background: precioConIgv ? 'rgba(96,165,250,0.18)' : 'transparent',
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    color: precioConIgv ? '#93c5fd' : '#64748b',
                    transition: 'all 0.15s ease',
                  }}>Precio c/IGV</button>
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={aplicarDescuentoCab}>
                Aplicar descuento a todas las líneas
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={agregarLinea}>+ Agregar línea</button>
            </div>
          </div>

          <div className="oc-detalle-wrap">
            <table className="oc-detalle-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }} title="Marcar como revisado">✓</th>
                  <th style={{ minWidth: 150 }}>Artículo</th>
                  <th style={{ width: 55 }}>Unidad</th>
                  <th style={{ width: 88 }}>Requerimiento</th>
                  <th style={{ width: 140 }}>Centro de Costo *</th>
                  <th className="right" style={{ width: 55 }}>Cant.</th>
                  <th className="right" style={{ width: 88 }}>Precio {precioConIgv ? 'c/IGV' : 's/IGV'} ({monedaSimbolo})</th>
                  <th className="right" style={{ width: 46 }}>%IGV</th>
                  <th className="right" style={{ width: 72 }}>IGV Unit.</th>
                  <th className="right" style={{ width: 78 }}>Total Unit.</th>
                  <th className="right" style={{ width: 48 }}>%Desc.</th>
                  <th className="right" style={{ width: 78 }}>Total Línea</th>
                  <th style={{ width: 110 }}>Observación</th>
                  <th style={{ width: 26 }}></th>
                </tr>
              </thead>
              <tbody>
                {detalle.length === 0 && (
                  <tr><td colSpan={14} style={{ textAlign: 'center', color: '#475569', padding: '24px' }}>Sin artículos. Haz clic en "+ Agregar línea" para comenzar.</td></tr>
                )}
                {detalle.map((linea, idx) => {
                  const esPEN = form.moneda === 'PEN';
                  const igvUnit = esPEN ? linea.igv_soles : linea.igv_dolares;
                  const totalUnit = esPEN ? linea.total_soles : linea.total_dolares;
                  const totalLinea = esPEN ? linea.total_con_descuento_soles : linea.total_con_descuento_dolares;
                  const precioCampo = esPEN ? 'precio_soles' : 'precio_dolares';
                  const precioSinIgv = esPEN ? linea.precio_soles : linea.precio_dolares;
                  const pctIgv = parseFloat(linea.porcentaje_igv) || 0;
                  // Valor mostrado en el input según el toggle
                  const precioDisplay = precioConIgv && parseFloat(precioSinIgv)
                    ? parseFloat((parseFloat(precioSinIgv) * (1 + pctIgv / 100)).toFixed(6))
                    : precioSinIgv;
                  const rqRef = linea.rq_numero
                    ? `${linea.rq_serie ? linea.rq_serie.trim() + '-' : ''}${linea.rq_numero}`
                    : null;
                  return (
                    <tr key={idx} style={linea.revisado ? { background: 'rgba(34,197,94,0.07)' } : {}}>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className={`oc-check-btn${linea.revisado ? ' checked' : ''}`}
                          title={linea.revisado ? 'Marcar como no revisado' : 'Marcar como revisado'}
                          onClick={() => toggleRevisado(idx, linea)}
                        >
                          {linea.revisado ? '✓' : '○'}
                        </button>
                      </td>
                      <td>
                        <AutoComplete
                          value={linea.id_articulo}
                          idField="id_articulo"
                          displayField={(a) => {
                            const cod = (a.codigo_interno || '').trim();
                            let desc = (a.descripcion || '').trim();
                            if (cod && desc.toUpperCase().startsWith(cod.toUpperCase())) {
                              desc = desc.slice(cod.length).replace(/^[\s\-–—·:]+/, '');
                            }
                            return `${cod ? cod + ' — ' : ''}${desc}`;
                          }}
                          asyncSearch={async (q) => { const r = await articuloService.search(q); return r.data || []; }}
                          minChars={3}
                          initialSelected={linea.id_articulo ? {
                            id_articulo: linea.id_articulo,
                            codigo_interno: linea.articulo_codigo,
                            descripcion: linea.articulo_descripcion,
                            unidad_descripcion: linea.unidad_descripcion,
                          } : null}
                          placeholder="Buscar (mín. 3)..."
                          onChange={(v, item) => setArticuloLinea(idx, item)}
                        />
                      </td>
                      <td>
                        <div className="oc-detalle-calc" style={{ textAlign: 'left', fontSize: 11, color: '#94a3b8' }}>
                          {linea.unidad_descripcion || '—'}
                        </div>
                      </td>
                      <td>
                        {rqRef ? (
                          <div className="oc-rq-ref" title={`Requerimiento: ${rqRef}`}>{rqRef}</div>
                        ) : (
                          <div className="oc-rq-ref empty">—</div>
                        )}
                      </td>
                      <td>
                        {linea.id_requerimiento_detalle ? (
                          <div
                            className="oc-detalle-calc"
                            style={{ textAlign: 'left', fontSize: 11, color: '#94a3b8', cursor: 'not-allowed' }}
                            title={linea.centro_costo_descripcion ? `${linea.centro_costo_codigo || ''} — ${linea.centro_costo_descripcion}` : 'Heredado del requerimiento'}
                          >
                            {linea.centro_costo_codigo
                              ? `${linea.centro_costo_codigo}${linea.centro_costo_descripcion ? ' — ' + linea.centro_costo_descripcion : ''}`
                              : (centrosCosto.find(c => c.id_centro_costo === linea.id_centro_costo)?.display_cc || '—')}
                          </div>
                        ) : (
                          <div style={!linea.id_centro_costo ? { outline: '1px solid rgba(239,68,68,0.5)', borderRadius: 4 } : {}}>
                            <AutoComplete
                              options={centrosCosto}
                              value={linea.id_centro_costo}
                              idField="id_centro_costo"
                              displayField="display_cc"
                              placeholder="CC..."
                              onChange={(v) => updateLinea(idx, 'id_centro_costo', v || null)}
                            />
                          </div>
                        )}
                      </td>
                      <td>
                        <input className="oc-detalle-input" type="number" step="0.0001" min="0"
                          value={linea.cantidad} onChange={e => updateLinea(idx, 'cantidad', e.target.value)} style={{ width: 64 }} />
                      </td>
                      <td>
                        <input className="oc-detalle-input" type="number" step="0.0001" min="0"
                          value={precioDisplay}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === '') { updateLinea(idx, precioCampo, ''); return; }
                            const n = parseFloat(v);
                            if (Number.isNaN(n)) return;
                            // Si el usuario captura con IGV, convertimos a sin IGV para almacenar
                            const sinIgv = precioConIgv ? n / (1 + pctIgv / 100) : n;
                            updateLinea(idx, precioCampo, sinIgv);
                          }}
                          onBlur={e => { if (!parseFloat(e.target.value)) updateLinea(idx, precioCampo, ''); }}
                          style={{ width: 108, ...(linea.id_articulo && !parseFloat(precioSinIgv) ? { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.15)' } : {}) }} />
                      </td>
                      <td>
                        <input className="oc-detalle-input" type="number" step="0.01" min="0" max="100"
                          value={linea.porcentaje_igv} onChange={e => updateLinea(idx, 'porcentaje_igv', e.target.value)} style={{ width: 54 }} />
                      </td>
                      <td><div className="oc-detalle-calc">{monedaSimbolo} {fmt4(igvUnit)}</div></td>
                      <td><div className="oc-detalle-calc">{monedaSimbolo} {fmt4(totalUnit)}</div></td>
                      <td>
                        <input className="oc-detalle-input" type="number" step="0.01" min="0" max="100"
                          value={linea.porcentaje_descuento} onChange={e => updateLinea(idx, 'porcentaje_descuento', e.target.value)} style={{ width: 54 }} />
                      </td>
                      <td><div className="oc-detalle-calc highlight">{monedaSimbolo} {fmt4(totalLinea)}</div></td>
                      <td>
                        <div className="oc-obs-cell">
                          <input
                            id={`oc-obs-${idx}`}
                            className="oc-detalle-input left"
                            type="text"
                            value={linea.observacion}
                            onChange={e => updateLinea(idx, 'observacion', e.target.value)}
                            placeholder="Observación..."
                            style={{ width: '100%' }}
                          />
                          <div className="oc-obs-tools">
                            <button type="button" className="oc-obs-btn bold" title="Negrita: **texto**" onClick={() => applyFmt(idx, linea.observacion, 'bold')}>B</button>
                            <button type="button" className="oc-obs-btn hl" title="Resaltar: [[texto]]" onClick={() => applyFmt(idx, linea.observacion, 'highlight')}>A</button>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button type="button" className="oc-detalle-del-btn" onClick={() => eliminarLinea(idx)}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="oc-totales">
            <div className="oc-totales-card">
              <div className="oc-total-row">
                <span className="oc-total-label">Base Imponible</span>
                <span className="oc-total-value">{monedaSimbolo} {fmtMon(form.moneda === 'PEN' ? totales.base_imponible_soles : totales.base_imponible_dolares, 4)}</span>
              </div>
              <div className="oc-total-row">
                <span className="oc-total-label">IGV</span>
                <span className="oc-total-value">{monedaSimbolo} {fmtMon(form.moneda === 'PEN' ? totales.igv_soles : totales.igv_dolares, 4)}</span>
              </div>
              {(form.moneda === 'PEN' ? totales.no_gravado_soles : totales.no_gravado_dolares) > 0 && (
                <div className="oc-total-row">
                  <span className="oc-total-label">No Gravados</span>
                  <span className="oc-total-value">{monedaSimbolo} {fmtMon(form.moneda === 'PEN' ? totales.no_gravado_soles : totales.no_gravado_dolares, 4)}</span>
                </div>
              )}
              {(form.moneda === 'PEN' ? totales.descuento_soles : totales.descuento_dolares) > 0 && (
                <div className="oc-total-row">
                  <span className="oc-total-label">Descuentos</span>
                  <span className="oc-total-value">− {monedaSimbolo} {fmtMon(form.moneda === 'PEN' ? totales.descuento_soles : totales.descuento_dolares, 4)}</span>
                </div>
              )}
              <div className="oc-total-row main">
                <span className="oc-total-label">Total a Pagar</span>
                <span className="oc-total-value">{monedaSimbolo} {fmtMon(form.moneda === 'PEN' ? totales.total_soles : totales.total_dolares, 2)}</span>
              </div>
              {form.moneda === 'USD' && (
                <div className="oc-total-row" style={{ marginTop: 6, borderTop: '1px dashed rgba(255,255,255,0.08)', paddingTop: 8 }}>
                  <span className="oc-total-label" style={{ color: '#64748b' }}>Equivalente en soles</span>
                  <span className="oc-total-value" style={{ color: '#64748b' }}>S/ {fmtMon(totales.total_soles, 2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Botones al pie */}
        <div className="oc-form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setView('list')}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit}>
            {editing ? 'Guardar Cambios' : 'Crear Orden de Compra'}
          </button>
        </div>
        </form>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODAL PROVEEDOR (crear nuevo desde OC, igual que en Requerimientos)
      ═══════════════════════════════════════════════════════════ */}
      {modalProv && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2>Proveedor / Cliente</h2>
                <span className="modal-header-badge">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              <button className="modal-close" onClick={() => setModalProv(false)}>&times;</button>
            </div>
            <form onSubmit={handleSaveProv}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Tipo de Documento</label>
                  <select value={formProv.tipo_documento} onChange={e => setFormProv(p => ({ ...p, tipo_documento: e.target.value }))}>
                    {TIPOS_DOC_ENTIDAD.map(t => <option key={t.codigo} value={t.codigo}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: formProv.tipo_documento === '6' ? '140px 1fr auto' : '140px 1fr', gap: '0 12px', alignItems: 'flex-end' }}>
                  <div className="form-row">
                    <label>Nro. Documento</label>
                    <input value={formProv.ruc} onChange={e => setFormProv(p => ({ ...p, ruc: e.target.value }))}
                      placeholder={formProv.tipo_documento === '6' ? 'RUC 11 dígitos' : 'Nro. documento'}
                      maxLength={formProv.tipo_documento === '6' ? 11 : 20} />
                  </div>
                  <div className="form-row">
                    <label>Razón Social / Nombre *</label>
                    <input value={formProv.razon_social} onChange={e => setFormProv(p => ({ ...p, razon_social: e.target.value }))} placeholder="Razón social o nombre" />
                  </div>
                  {formProv.tipo_documento === '6' && (
                    <button type="button" className="btn btn-secondary" onClick={handleConsultarRuc}
                      disabled={consultandoRuc} style={{ marginBottom: 18, whiteSpace: 'nowrap' }}>
                      {consultandoRuc
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
                        : <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>}
                    </button>
                  )}
                </div>
                <div className="form-row">
                  <label>Dirección</label>
                  <input value={formProv.direccion} onChange={e => setFormProv(p => ({ ...p, direccion: e.target.value }))} placeholder="Dirección" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Correo</label>
                    <input value={formProv.correo1} onChange={e => setFormProv(p => ({ ...p, correo1: e.target.value }))} placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="form-row">
                    <label>Teléfono</label>
                    <input value={formProv.telefono_contacto} onChange={e => setFormProv(p => ({ ...p, telefono_contacto: e.target.value }))} placeholder="Teléfono" />
                  </div>
                  <div className="form-row">
                    <label>Celular</label>
                    <input value={formProv.celular} onChange={e => setFormProv(p => ({ ...p, celular: e.target.value }))} placeholder="Celular" />
                  </div>
                </div>
                <div className="form-row">
                  <label>Persona de Contacto</label>
                  <input value={formProv.persona_contacto} onChange={e => setFormProv(p => ({ ...p, persona_contacto: e.target.value }))} placeholder="Nombre del contacto" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalProv(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={savingProv}>
                  {savingProv ? 'Guardando...' : 'Crear Proveedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
}
